import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { demofile } from '../utils/paths';
import {
  ArrowUp,
  ArrowLeft,
  Lightbulb,
  Volume2,
  VolumeX,
  ChevronDown,
  X,
  Play,
  Pause,
  Music,
  Mic,
  Type,
  ChevronLeft,
  ChevronRight,
  Video,
  SlidersHorizontal,
  CheckCircle2,
  Star
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getT } from '../utils/translations';
import { trackEvent } from '../utils/gtag';
import LazyVideo from './LazyVideo';

const TABS_BASE = [
  {
    id: 'gen',
    gradient: 'from-blue-50 to-indigo-50',
    activeBorder: 'border-blue-500',
    activeDot: 'bg-blue-500',
    activeBg: 'bg-blue-50'
  },
  {
    id: 'edit',
    gradient: 'from-purple-50 to-pink-50',
    activeBorder: 'border-purple-500',
    activeDot: 'bg-purple-500',
    activeBg: 'bg-purple-50'
  },
];

// DATA CONSTANTS
// 文生音效示例：与介绍页保持一致，并为每个示例配置 prompt / 模型 / 时长 / 条数
const TEXT_EXAMPLES = [
  { id: 't1',  text: "小河流水", textEn: "Stream",       icon: '🏞️', url: demofile("mp3/小河流水.flac"),    prompt: "纯净舒缓的河水流声，高保真录音，清晰缓慢的潺潺声，无噪音、无鸟鸣或其他环境音，柔和自然的流水质感。", promptEn: "Pure and soothing river water flowing, hi-fi recording, clear and slow babbling, no noise, no birdsong or other ambient sounds, soft and natural water texture.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't2',  text: "雨滴声",   textEn: "Rain",         icon: '🌧️', url: demofile("mp3/雨滴声.flac"),  prompt: "雨滴落在屋檐和窗户上，发出清脆的敲击声，没有背景噪音，真实清晰。", promptEn: "Raindrops falling on eaves and windows, crisp tapping sounds, no background noise, realistic and clear.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't3',  text: "海浪声",   textEn: "Ocean Waves",  icon: '🌊', url: demofile("mp3/海浪声.flac"),     prompt: "轻柔的海浪一波接一波地涌来，持续不断的有节奏的潮起潮落，高保真音质，无噪音或环境音。", promptEn: "Gentle ocean waves rolling in one after another, continuous rhythmic tides, hi-fi quality, no noise or ambient sounds.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't4',  text: "虫鸣",     textEn: "Insects",      icon: '🦗', url: demofile("mp3/虫鸣.flac"),   prompt: "森林里静谧的夜晚，蟋蟀轻柔而持续地鸣叫，营造出平静舒缓的氛围，节奏缓慢，音量低，令人放松的。", promptEn: "A quiet night in the forest, crickets chirping softly and continuously, creating a calm and soothing atmosphere, slow rhythm, low volume, relaxing.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't5',  text: "篝火",     textEn: "Campfire",     icon: '🔥', url: demofile("mp3/篝火.flac"),  prompt: "轻柔的篝火噼啪声，柔和的木柴燃烧爆裂声，温暖纯净的环境音，清晰自然的音频。", promptEn: "Gentle campfire crackling, soft wood burning and popping, warm and pure ambient sound, clear and natural audio.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't6',  text: "雷声",     textEn: "Thunder",      icon: '⚡', url: demofile("mp3/雷声.flac"),   prompt: "猛烈的雷声伴有巨大的爆炸声，强烈的暴风雷声，震撼人心的环境音效。", promptEn: "Intense thunder with massive booming, powerful storm thunder, awe-inspiring ambient sound effects.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't7',  text: "吉他",     textEn: "Guitar",       icon: '🎸', url: demofile("mp3/吉他.flac"),    prompt: "专业指弹吉他演奏，清晰细腻的拨弦，旋律优美、层次丰富的音色，优雅的器乐作品。", promptEn: "Professional fingerstyle guitar performance, clear and delicate plucking, beautiful melody with rich tonal layers, elegant instrumental piece.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't8',  text: "架子鼓",   textEn: "Drums",        icon: '🥁', url: demofile("mp3/架子鼓.flac"),     prompt: "动感十足的流行鼓组，欢快的节奏，清晰有力的鼓点节奏，现代而充满活力，纯正的打击乐音效。", promptEn: "Energetic pop drum kit, upbeat rhythm, clear and powerful beats, modern and vibrant, authentic percussion sound.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't9',  text: "钢琴",     textEn: "Piano",        icon: '🎹', url: demofile("mp3/钢琴.flac"),     prompt: "干净利落的钢琴演奏，明快欢快的旋律，清澈的音色，轻快愉悦的节奏，高清晰度的音频。", promptEn: "Clean and crisp piano performance, bright and cheerful melody, clear tone, light and joyful rhythm, high-definition audio.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't10', text: "羊叫",     textEn: "Sheep",        icon: '🐑', url: demofile("mp3/羊叫.flac"),     prompt: "单只绵羊的叫声，纯净而逼真。", promptEn: "A single sheep bleating, pure and realistic.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't11', text: "狗叫",     textEn: "Dog",          icon: '🐶', url: demofile("mp3/狗叫.flac"),       prompt: "清晰的狗吠声，轻柔悦耳，干净的声音，没有背景噪音。", promptEn: "Clear dog barking, gentle and pleasant, clean sound, no background noise.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't12', text: "猫叫",     textEn: "Cat",          icon: '🐱', url: demofile("mp3/猫叫.flac"),       prompt: "猫叫声，没有背景噪音", promptEn: "Cat meowing, no background noise.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't13', text: "脚步声",   textEn: "Footsteps",    icon: '👣', url: demofile("mp3/脚步声.flac"), prompt: "木地板上清晰清脆的脚步声，自然干净的声音，没有背景噪音。", promptEn: "Clear and crisp footsteps on wooden floor, natural and clean sound, no background noise.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't14', text: "打字声",   textEn: "Typing",       icon: '⌨️', url: demofile("mp3/键盘打字声.flac"),    prompt: "柔和清脆的机械键盘敲击声，清晰干净的音频，无背景噪音。", promptEn: "Soft and crisp mechanical keyboard typing, clear and clean audio, no background noise.", model: 'ControlFoley', duration: 5, count: 1 },
  { id: 't15', text: "打呼噜",   textEn: "Snoring",      icon: '😴', url: demofile("mp3/呼噜声.flac"),     prompt: "深沉而平静的人类鼾声，逼真而舒缓，营造安静的睡眠环境音。", promptEn: "Deep and calm human snoring, realistic and soothing, creating a quiet sleep ambient sound.", model: 'ControlFoley', duration: 5, count: 1 },
];

const VIDEO_EXAMPLES = [
  { id: 'v1', title: "", url: demofile("mp4/" + encodeURIComponent("崖畔海韵.mp4")), desc: "" },
  { id: 'v2', title: "", url: demofile("mp4/" + encodeURIComponent("哥斯拉金刚.mp4")), desc: "" },
  { id: 'v3', title: "", url: demofile("mp4/" + encodeURIComponent("摩托赛车.mp4")), desc: "" },
  { id: 'v4', title: "", url: demofile("mp4/" + encodeURIComponent("ASMR倒草莓.mp4")), desc: "" },
  { id: 'v5', title: "", url: demofile("mp4/" + encodeURIComponent("啤酒广告音效.mp4")), desc: "" }
];

const REF_EXAMPLES = [
  { id: 'r1', title: "音效迁移", url: demofile("mp4/" + encodeURIComponent("dog_cat.mp4")), desc: "狗叫生成猫叫", audioUrl: demofile("mp3/cat.wav") },
  { id: 'r2', title: "新增参考素材", url: demofile("mp4/" + encodeURIComponent("参考视频.mp4")), desc: "参考视频 + 参考音频", audioUrl: demofile("mp3/" + encodeURIComponent("参考音频.wav")) }
];

// Try Now — {t.experience.tryDemos.textTitle} under the Text mode only
const TEXT_TRY_DEMOS = [
  { id: 'td1', title: 'Birds singing in a forest', url: demofile("mp3/" + encodeURIComponent("A_bird_sings_melodically_in_a_forest.flac")) },
  { id: 'td2', title: 'Motorcycle idle and accelerate', url: demofile("mp3/" + encodeURIComponent("摩托车引擎.flac")) },
  { id: 'td3', title: 'Bonfire burning', url: demofile("mp3/" + encodeURIComponent("篝火.flac")) },
  { id: 'td4', title: 'Rhythmic guitar', url: demofile("mp3/" + encodeURIComponent("吉他.flac")) },
];

// Try Now — {t.experience.tryDemos.videoTitle}
const VIDEO_TRY_DEMOS = [
  { id: 'vd1', url: demofile("轮播demo/" + encodeURIComponent("tv2a_skateboarding.mp4")) },
  { id: 'vd2', url: demofile("轮播demo/" + encodeURIComponent("tv2a_playing_drums.mp4")) },
];

// Try Now — {t.experience.tryDemos.textCtrlTitle}
const TEXTCTRL_TRY_DEMOS = [
  { id: 'tvd1', url: demofile("TC-V2A_infer/" + encodeURIComponent("3sz7nQTcwcE_000009.mp4")), prompt: 'chainsaw idling' },
  { id: 'tvd2', url: demofile("TC-V2A_infer/" + encodeURIComponent("tcv2a_magic_thunder_strike.mp4")), prompt: 'thunder strike' },
];

// Try Now — {t.experience.tryDemos.refTitle} (video + reference audio)
const AC_TRY_DEMOS = [
  {
    id: 'acd1',
    videoUrl: demofile("AC-V2A_infer/example1/" + encodeURIComponent("acv2a_sword_clashhit_prior.mp4")),
    audioUrl: demofile("AC-V2A_infer/example1/sword_clashhit_prior.mp3"),
    title: 'Sword clashhit (input for reference)'
  },
  {
    id: 'acd2',
    videoUrl: demofile("AC-V2A_infer/example2/" + encodeURIComponent("38a84808-c0d0-4b81-8f45-f87bf3afd16d_2.mp4")),
    audioUrl: demofile("AC-V2A_infer/example2/music.mp3"),
    title: 'Playing the guitar (input for reference)'
  }
];

const PRIMARY_MODEL = 'ControlFoley';
const MODELS = [PRIMARY_MODEL];

const MODEL_BACKEND_MAPPING: {[key: string]: string} = {
  [PRIMARY_MODEL]: 'ControlFoley'
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://controlfoley.ai.xiaomi.com';
const API_BASE_URL = `${API_BASE}/api/v1/v2a`;
const PROCESS_API_URL = `${API_BASE_URL}/submit`;
const STATUS_API_URL = `${API_BASE_URL}/status`;
const MODELS_API_URL = `${API_BASE_URL}/models`;

const rewriteResultUrls = (urls: string[]): string[] =>
  urls.map(u => {
    try {
      const parsed = new URL(u);
      if (parsed.hostname.endsWith('.xiaomi.srv')) {
        return `${API_BASE}${parsed.pathname}`;
      }
    } catch { /* not a valid URL, return as-is */ }
    return u;
  });

// 单次生成记录，用于在右侧输出区域展示包含提示词和配置的历史记录
interface GenerationRecord {
  id: string;
  mode: 'text' | 'video_plain' | 'video_text' | 'ref';
  prompt: string;
  negativePrompt?: string;
  model: string;
  durationMode: 'seconds' | 'smart';
  duration: number;
  count: number;
  createdAt: string;
  urls: string[];
  status: 'pending' | 'success' | 'failed';
  error?: string;
}

interface AIExperiencePageProps {
  onBack: () => void;
  initialTab?: string;
}

export default function AIExperiencePage({ onBack, initialTab = 'gen' }: AIExperiencePageProps) {
  const { lang } = useLanguage();
  const t = getT(lang);
  const safeInitialTab: 'gen' | 'edit' =
    initialTab === 'edit' ? 'edit' : 'gen';
  const isTryNowMode = String(initialTab ?? '').includes('tryNow');
  const TABS = TABS_BASE.map((tab) => ({
    ...tab,
    name: t.experience.tabs[tab.id as keyof typeof t.experience.tabs].name,
    sub:  t.experience.tabs[tab.id as keyof typeof t.experience.tabs].sub,
  }));
  // “立即体验”入口只应展示音效生成推理界面（隐藏其他推理模块）
  const showEditTab = safeInitialTab === 'edit';
  const [activeTab, setActiveTab] = useState<'gen' | 'edit'>(safeInitialTab);
  const [showExamples, setShowExamples] = useState(!isTryNowMode);
  const [showPromptGuide, setShowPromptGuide] = useState(false);

  // 立即体验：固定单一模型（不提供模型选择UI）
  useEffect(() => {
    if (!isTryNowMode) return;
    setConfig((prev) => ({ ...prev, model: PRIMARY_MODEL }));
  }, [isTryNowMode]);
  // 音效生成子模块：文生音效 / 视频生音效 / 参考音频生音效
  const [genMode, setGenMode] = useState<'text' | 'video' | 'ref'>('text');
  // videoTab: 区分 Video-to-Audio 的两个同内容子模式：普通/文本控制
  const [videoTab, setVideoTab] = useState<'plain' | 'textCtrl'>('plain');

  // Settings Config State
  const [showConfig, setShowConfig] = useState(false);
  const configRef = useRef<HTMLDivElement>(null);
  const [config, setConfig] = useState({
    model: PRIMARY_MODEL,
    durationMode: 'seconds', // legacy: keep for edit tab
    duration: 5,
    count: 4,
    editMode: 'extract', // 'extract' | 'delete' | 'replace' | 'add'
  });

  // Per-mode generation config for the four modes in the Gen tab
  type ModeKey = 'text' | 'video_plain' | 'video_text' | 'ref';
  const [modeCfg, setModeCfg] = useState<Record<ModeKey, { durationMode: 'seconds' | 'smart'; duration: number; count: number }>>({
    text: { durationMode: 'seconds', duration: 5, count: 4 },
    video_plain: { durationMode: 'smart', duration: 5, count: 4 },
    video_text: { durationMode: 'smart', duration: 5, count: 4 },
    ref: { durationMode: 'smart', duration: 5, count: 4 },
  });


  // Derived current mode key for Gen tab and per-mode config sync
  const currentGenKey: ModeKey = genMode === 'text' ? 'text' : (genMode === 'video' ? (videoTab === 'textCtrl' ? 'video_text' : 'video_plain') : 'ref');
  const curGenCfg = modeCfg[currentGenKey];

  // Keep Gen tab's shared config in sync with per-mode settings for the active mode
  const syncingRef = useRef<null | 'fromModeCfg'>(null);
  useEffect(() => {
    if (activeTab !== 'gen') return;
    syncingRef.current = 'fromModeCfg';
    setConfig(prev => ({
      ...prev,
      durationMode: curGenCfg.durationMode,
      duration: curGenCfg.duration,
      count: curGenCfg.count,
    }));
  }, [activeTab, currentGenKey]);

  // When user adjusts sliders/toggles in Gen tab, persist to per-mode storage
  useEffect(() => {
    if (activeTab !== 'gen') return;
    if (syncingRef.current === 'fromModeCfg') {
      syncingRef.current = null;
      return;
    }
    setModeCfg(prev => ({
      ...prev,
      [currentGenKey]: {
        ...prev[currentGenKey],
        durationMode: config.durationMode,
        duration: config.duration,
        count: config.count,
      }
    }));
  }, [config.durationMode, config.duration, config.count, activeTab, currentGenKey]);

  // Update activeTab when initialTab prop changes
  useEffect(() => {
    setActiveTab(initialTab === 'edit' ? 'edit' : 'gen');
  }, [initialTab]);

  // Scroll to top when component mounts or when initialTab changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [initialTab]);

  // 获取可用模型列表
  const [availableModels, setAvailableModels] = useState<string[]>(MODELS);
  const [modelsLoadError, setModelsLoadError] = useState<string | null>(null);
  
  useEffect(() => {
    // 尝试从 API 获取模型列表
    const fetchModels = async () => {
      setModelsLoadError(null);
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5秒超时
        
        const res = await fetch(MODELS_API_URL, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (res.ok) {
          const data = await res.json();
          if (data.models && Array.isArray(data.models)) {
            const enabledBackendModels = data.models
              .filter((m: { name: string; enabled: boolean }) => m.enabled)
              .map((m: { name: string }) => m.name);
            
            // Filter MODELS based on backend availability using the mapping
            const enabledFrontendModels = MODELS.filter(model => {
              // 临时策略：强制 Transfer V2 可用，确保在参考音频模式下可选
              if (model === 'Transfer V2') return true;
              return enabledBackendModels.includes(MODEL_BACKEND_MAPPING[model]);
            });

            if (enabledFrontendModels.length > 0) {
              setAvailableModels(enabledFrontendModels);
              // 如果当前选择的模型不在可用列表中，切换到第一个可用模型
              if (!enabledFrontendModels.includes(config.model)) {
                setConfig(prev => ({ ...prev, model: enabledFrontendModels[0] }));
              }
            }
          }
        } else {
          // HTTP 错误
          const errorText = await res.text().catch(() => '');
          let errorMsg = `HTTP ${res.status}`;
          try {
            const errorData = JSON.parse(errorText);
            errorMsg = errorData.detail || errorData.message || errorMsg;
          } catch {
            if (errorText) errorMsg = errorText;
          }
          setModelsLoadError(`获取模型列表失败 (${errorMsg})`);
        }
      } catch (error) {
        console.warn('获取模型列表失败，使用默认模型列表:', error);
        
        let errorMessage = '未知错误';
        if (error instanceof Error) {
          errorMessage = error.message;
          
          // 处理不同类型的错误
          if (error.name === 'AbortError') {
            errorMessage = '请求超时，请检查网络连接';
          } else if (errorMessage.includes('Failed to fetch') || 
                     errorMessage.includes('NetworkError') ||
                     errorMessage.includes('Load failed')) {
            errorMessage = `无法连接到服务器，请确认后端服务可达 (${API_BASE})`;
          } else if (errorMessage.includes('CORS')) {
            errorMessage = 'CORS 错误：请检查后端服务器的 CORS 配置';
          }
        }
        
        setModelsLoadError(`获取模型列表失败: ${errorMessage}`);
        // 失败时使用默认模型列表，不影响主要功能
      }
    };
    
    if (!isTryNowMode && activeTab === 'gen') {
      fetchModels();
    }
  }, [activeTab]);

  // 根据生成模式限制可选模型：
  // - 文生音效、视频生音效：仅允许使用常量 MODELS 列表中的模型（Transfer V2除外）
  // - 参考音频生音效：仅支持自研2.0 和 Transfer V2
  useEffect(() => {
    if (activeTab !== 'gen') return;
    if (genMode === 'ref') {
      if (config.model !== PRIMARY_MODEL && config.model !== 'Transfer V2') {
        setConfig(prev => ({ ...prev, model: PRIMARY_MODEL }));
      }
    } else {
      // 文生音效 / 视频生音效：Transfer V2 不可用
      if (config.model === 'Transfer V2') {
        setConfig(prev => ({ ...prev, model: PRIMARY_MODEL }));
      }
      // 如果当前模型不在允许列表中，强制切回主模型
      else if (!MODELS.includes(config.model)) {
        setConfig(prev => ({ ...prev, model: PRIMARY_MODEL }));
      }
    }
  }, [activeTab, genMode, config.model]);

  // Carousel States
  const [refIndex, setRefIndex] = useState(0);
  const refVideoPlayerRef = useRef<HTMLVideoElement | null>(null);
  const [isRefVideoAudioPlaying, setIsRefVideoAudioPlaying] = useState(false);
  
  // Video examples states - 每个视频独立的播放状态和 ref
  const videoPlayerRefs = useRef<{ [key: string]: HTMLVideoElement | null }>({});
  const [videoAudioPlayingStates, setVideoAudioPlayingStates] = useState<{ [key: string]: boolean }>({});

  // Reset ref video audio playing state when ref video changes
  useEffect(() => {                                                                                                                                                          
    setIsRefVideoAudioPlaying(false);                                                                                                                                 
    if (refVideoPlayerRef.current) {                                                                                                                                         
      refVideoPlayerRef.current.muted = true;                                                                                                                         
    }                                                                                                                                                                 
  }, [refIndex]); 

  // Audio Playback State
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<{[key: string]: HTMLAudioElement}>({});
  const [audioDurations, setAudioDurations] = useState<{[key: string]: number}>({});
  const [audioProgress, setAudioProgress] = useState<{[key: string]: number}>({});
  const [audioCurrentTime, setAudioCurrentTime] = useState<{[key: string]: number}>({});

  // Text Input Ref
  const positivePromptRef = useRef<HTMLTextAreaElement>(null);

  // File Upload Refs and State（视频生音效与参考音频生音效分开，互不显示）
  const videoInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const videoUploadTargetRef = useRef<'video_plain' | 'video_text' | 'ref'>(null);
  const refModeVideoInputRef = useRef<HTMLInputElement>(null);
  // 视频生音效专用
  // Video-to-Audio（plain）
  const [videoModeVideoFile, setVideoModeVideoFile] = useState<File | null>(null);
  const [videoModeVideoPreviewUrl, setVideoModeVideoPreviewUrl] = useState<string | null>(null);
  const [videoModeVideoError, setVideoModeVideoError] = useState(false);
  // Text-Controlled Video-to-Audio（textCtrl）
  const [textCtrlVideoFile, setTextCtrlVideoFile] = useState<File | null>(null);
  const [textCtrlVideoPreviewUrl, setTextCtrlVideoPreviewUrl] = useState<string | null>(null);
  const [textCtrlVideoError, setTextCtrlVideoError] = useState(false);
  // 参考音频生音效专用
  const [refModeVideoFile, setRefModeVideoFile] = useState<File | null>(null);
  const [refModeVideoPreviewUrl, setRefModeVideoPreviewUrl] = useState<string | null>(null);
  const [refModeVideoError, setRefModeVideoError] = useState(false);
  const [refModeAudioFile, setRefModeAudioFile] = useState<File | null>(null);
  const [refModeAudioPreviewUrl, setRefModeAudioPreviewUrl] = useState<string | null>(null);
  // 三个模块的提示/负向提示隔离，互不显示
  const [textModePrompt, setTextModePrompt] = useState('');
  const [textModeNegativePrompt, setTextModeNegativePrompt] = useState('');
  const [videoModePrompt, setVideoModePrompt] = useState('');
  const [videoModeNegativePrompt, setVideoModeNegativePrompt] = useState('');
  const [textCtrlPrompt, setTextCtrlPrompt] = useState('');
  const [textCtrlNegativePrompt, setTextCtrlNegativePrompt] = useState('');
  const [refModePrompt, setRefModePrompt] = useState('');
  const [refModeNegativePrompt, setRefModeNegativePrompt] = useState('');
  // edit 标签页用
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);

  // Function to clear all inputs（在 state 声明之后定义）
  const clearAllInputs = useCallback(() => {
    if (positivePromptRef.current) {
      positivePromptRef.current.value = '';
    }
    setTextModePrompt('');
    setTextModeNegativePrompt('');
    setVideoModePrompt('');
    setVideoModeNegativePrompt('');
    setRefModePrompt('');
    setRefModeNegativePrompt('');
    if (videoModeVideoPreviewUrl) URL.revokeObjectURL(videoModeVideoPreviewUrl);
    setVideoModeVideoFile(null);
    setVideoModeVideoPreviewUrl(null);
    setVideoModeVideoError(false);
    if (refModeVideoPreviewUrl) URL.revokeObjectURL(refModeVideoPreviewUrl);
    setRefModeVideoFile(null);
    setRefModeVideoPreviewUrl(null);
    setRefModeVideoError(false);
    if (refModeAudioPreviewUrl) URL.revokeObjectURL(refModeAudioPreviewUrl);
    setRefModeAudioFile(null);
    setRefModeAudioPreviewUrl(null);
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setVideoFile(null);
    setVideoPreviewUrl(null);
    if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    setAudioFile(null);
    setAudioPreviewUrl(null);
  }, [videoModeVideoPreviewUrl, refModeVideoPreviewUrl, refModeAudioPreviewUrl, videoPreviewUrl, audioPreviewUrl]);

  const clearAllInputsRef = useRef(clearAllInputs);
  clearAllInputsRef.current = clearAllInputs;

  // 仅在切换 activeTab 时清空输入并更新配置（不依赖 clearAllInputs，避免上传后因 clearAllInputs 引用变化再次执行导致预览被清空）
  useEffect(() => {
    clearAllInputsRef.current();
    setFileSizeWarning({ text: null, video_plain: null, video_text: null, ref: null });
    if (activeTab === 'edit') {
      setConfig(prev => ({ ...prev, durationMode: 'smart' }));
    } else if (activeTab === 'gen') {
      setConfig(prev => ({ ...prev, durationMode: 'seconds' }));
    }
  }, [activeTab]);
  
  const negativePromptRef = useRef<HTMLTextAreaElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // 任务提交成功：toast 仅在该提交模块下显示 2s，切换模块不显示，过期后回切也不显示
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastForMode, setToastForMode] = useState<'text' | 'video' | 'ref' | null>(null);
  const [textCooldownRemaining, setTextCooldownRemaining] = useState(0);
  const [videoCooldownRemaining, setVideoCooldownRemaining] = useState(0);
  const [refCooldownRemaining, setRefCooldownRemaining] = useState(0);
  // 所有历史生成记录（包含提示词、配置和对应的音视频结果）
  const [generatedResults, setGeneratedResults] = useState<GenerationRecord[]>([]);
  // 视频生音效：每条记录当前选中的结果下标（1、2、3、4 对应 0、1、2、3）
  const [selectedVideoResultIndex, setSelectedVideoResultIndex] = useState<Record<string, number>>({});
  // 当前正在生成的记录，用于在历史记录上方展示“创作中”模块
  const [pendingRecordId, setPendingRecordId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiErrorFor, setApiErrorFor] = useState<'text' | 'video_plain' | 'video_text' | 'ref' | null>(null);
  const [fileSizeWarning, setFileSizeWarning] = useState<Record<ModeKey, string | null>>({
    text: null, video_plain: null, video_text: null, ref: null,
  });
  const MAX_TOTAL_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  const MAX_MEDIA_DURATION = 30; // 30秒

  // 校验文件总大小（video + audio），超出时设置对应模式的警告
  const checkTotalFileSize = (mode: ModeKey, videoFile: File | null, audioFile: File | null) => {
    const total = (videoFile?.size || 0) + (audioFile?.size || 0);
    if (total > MAX_TOTAL_FILE_SIZE) {
      const sizeMB = (total / (1024 * 1024)).toFixed(1);
      setFileSizeWarning(prev => ({
        ...prev,
        [mode]: lang === 'zh'
          ? `上传文件总大小 ${sizeMB}MB，超过 100MB 限制`
          : `Total file size ${sizeMB}MB exceeds the 100MB limit`,
      }));
    } else {
      setFileSizeWarning(prev => ({ ...prev, [mode]: null }));
    }
  };

  // 校验音视频时长，超过30秒则拒绝上传并弹窗提示
  const checkMediaDuration = (file: File, _mode: ModeKey): Promise<boolean> => {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const isVideo = file.type.startsWith('video/');
      const el = document.createElement(isVideo ? 'video' : 'audio');
      el.preload = 'metadata';
      el.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        if (el.duration > MAX_MEDIA_DURATION) {
          const msg = lang === 'zh'
            ? `仅支持时长小于 30 秒的音视频，当前时长 ${el.duration.toFixed(1)} 秒`
            : `Only audio/video under 30 seconds is supported, current duration ${el.duration.toFixed(1)}s`;
          setToastMessage(msg);
          setToastForMode(null);
          setToastShow(true);
          window.setTimeout(() => { setToastShow(false); setToastForMode(null); }, 3000);
          resolve(false);
        } else {
          resolve(true);
        }
      };
      el.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(true); // 无法检测时长时放行
      };
      el.src = url;
    });
  };

  // 异步任务状态管理
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<'submitting' | 'pending' | 'processing' | 'success' | 'failed' | null>(null);
  const [queuePos, setQueuePos] = useState<number | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // 左侧看板 ref，用于同步右侧看板高度（自动识别左侧高度）
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState<number | null>(null);

  // 监听左侧看板高度，同步为右侧看板固定高度（用元素完整视觉高度，含 padding/border）
  useEffect(() => {
    if (activeTab !== 'gen' || !leftPanelRef.current) return;
    const el = leftPanelRef.current;
    const updateHeight = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setLeftPanelHeight(h);
    };
    const ro = new ResizeObserver(() => updateHeight());
    ro.observe(el);
    // 初次与布局完成后各测一次，避免首帧未撑开导致右侧偏矮
    updateHeight();
    requestAnimationFrame(() => updateHeight());
    return () => ro.disconnect();
  }, [activeTab, genMode]);

  // 三个模块各自的 3s 冷却倒计时
  useEffect(() => {
    const hasCooldown = textCooldownRemaining > 0 || videoCooldownRemaining > 0 || refCooldownRemaining > 0;
    if (!hasCooldown) return;
    const t = setInterval(() => {
      setTextCooldownRemaining(prev => (prev <= 0 ? 0 : prev - 1));
      setVideoCooldownRemaining(prev => (prev <= 0 ? 0 : prev - 1));
      setRefCooldownRemaining(prev => (prev <= 0 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [textCooldownRemaining, videoCooldownRemaining, refCooldownRemaining]);

  // Click outside to close settings
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (configRef.current && !configRef.current.contains(event.target as Node)) {
        setShowConfig(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const togglePlay = (id: string, url: string) => {
    // 1. Stop any other playing audio
    if (playingId && playingId !== id) {
       const prevAudio = audioRefs.current[playingId];
       if (prevAudio) {
         prevAudio.pause();
         prevAudio.currentTime = 0;
       }
       setPlayingId(null);
       setAudioCurrentTime(prev => ({ ...prev, [playingId]: 0 }));
       setAudioProgress(prev => ({ ...prev, [playingId]: 0 }));
    }

    // 2. If clicking the currently playing item, pause it
    if (playingId === id) {
      audioRefs.current[id]?.pause();
      setPlayingId(null);
      return;
    }

    // 3. Initialize and play new audio
    if (!audioRefs.current[id]) {
        const audio = new Audio(url);
        
        // Simple error handling
        audio.onerror = (e) => {
             console.error(`Error loading audio file at: ${url}`, e);
             setPlayingId(null);
        };

        audio.onended = () => {
          setPlayingId(null);
          setAudioCurrentTime(prev => ({ ...prev, [id]: 0 }));
          setAudioProgress(prev => ({ ...prev, [id]: 0 }));
        };

        audio.onloadedmetadata = () => {
          setAudioDurations(prev => ({
            ...prev,
            [id]: audio.duration
          }));
        };
        
        audioRefs.current[id] = audio;
        
        audio.play()
            .then(() => setPlayingId(id))
            .catch(e => {
                console.error("Playback interrupted or failed", e);
                setPlayingId(null);
            });
    } else {
        // 4. Resume existing audio instance
        const audio = audioRefs.current[id];
        audio.currentTime = 0; // Restart from beginning
        setAudioCurrentTime(prev => ({ ...prev, [id]: 0 }));
        setAudioProgress(prev => ({ ...prev, [id]: 0 }));
        audio.play()
            .then(() => setPlayingId(id))
            .catch(e => console.error("Resume error", e));
    }
  };

  // Carousel Handlers
  const nextRef = () => setRefIndex((prev) => (prev + 1) % REF_EXAMPLES.length);
  const prevRef = () => setRefIndex((prev) => (prev - 1 + REF_EXAMPLES.length) % REF_EXAMPLES.length);
  
  // Video example audio toggle handler
  const toggleVideoAudio = (videoId: string) => {
    const videoEl = videoPlayerRefs.current[videoId];
    if (videoEl) {
      const isPlaying = videoAudioPlayingStates[videoId] || false;
      if (isPlaying) {
        videoEl.muted = true;
        setVideoAudioPlayingStates(prev => ({ ...prev, [videoId]: false }));
      } else {
        videoEl.muted = false;
        setVideoAudioPlayingStates(prev => ({ ...prev, [videoId]: true }));
        videoEl.play().catch(() => {
          setVideoAudioPlayingStates(prev => ({ ...prev, [videoId]: false }));
        });
      }
    }
  };

  // Load audio durations
  useEffect(() => {
    TEXT_EXAMPLES.forEach((item) => {
      if (!audioDurations[item.id]) {
        const audio = new Audio(item.url);
        audio.addEventListener('loadedmetadata', () => {
          setAudioDurations(prev => ({
            ...prev,
            [item.id]: audio.duration
          }));
        });
        audio.addEventListener('error', () => {
          setAudioDurations(prev => ({
            ...prev,
            [item.id]: 10 // Default fallback
          }));
        });
      }
    });
  }, []);

  // Track audio progress
  useEffect(() => {
    const interval = setInterval(() => {
      Object.keys(audioRefs.current).forEach((id) => {
        const audio = audioRefs.current[id];
        if (audio && !audio.paused) {
          setAudioCurrentTime(prev => ({
            ...prev,
            [id]: audio.currentTime
          }));
          setAudioProgress(prev => ({
            ...prev,
            [id]: (audio.currentTime / audio.duration) * 100
          }));
        }
      });
    }, 100);

    return () => clearInterval(interval);
  }, [playingId]);

  // Format time to MM:SS
  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // 轮询任务状态
  const pollTaskStatus = async (
    taskId: string,
    meta?: {
      mode: 'text' | 'video_plain' | 'video_text' | 'ref';
      prompt: string;
      negativePrompt?: string;
      model: string;
      durationMode: 'seconds' | 'smart';
      duration: number;
      count: number;
    },
    recordId?: string
  ) => {
    try {
      const res = await fetch(`${STATUS_API_URL}/${taskId}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      
      setTaskStatus(data.status);
      
      if (data.status === 'pending' || data.status === 'processing') {
        setQueuePos((data.queue_position ?? data.queue_pos) || null);
        // 继续轮询
        pollingIntervalRef.current = setTimeout(() => {
          pollTaskStatus(taskId, meta, recordId);
        }, 2000); // 每2秒轮询一次
      } else if (data.status === 'success') {
        // 任务完成
        setQueuePos(null);
        const rawUrls = Array.isArray(data.urls) ? data.urls 
                    : Array.isArray(data.processed_urls) ? data.processed_urls 
                    : [];
        const urls = rewriteResultUrls(rawUrls);

        // 将本次任务结果整理成一条记录，追加到历史列表中
        if (meta && urls.length > 0 && recordId) {
          const record: GenerationRecord = { // mode already split into video_plain/video_text
            id: recordId,
            mode: meta.mode,
            prompt: meta.prompt,
            negativePrompt: meta.negativePrompt,
            model: meta.model,
            durationMode: meta.durationMode,
            duration: meta.duration,
            count: meta.count,
            createdAt: new Date().toISOString(),
            urls,
            status: 'success',
          };
          // 更新对应记录为成功并填充结果
          setGeneratedResults(prev =>
            prev.map(r => (r.id === recordId ? record : r))
          );
        }
        setIsGenerating(false);
        setTaskId(null);
        setPendingRecordId(null);
        if (pollingIntervalRef.current) {
          clearTimeout(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (data.status === 'failed') {
        // 任务失败
        setQueuePos(null);
        const errorMsg = data.error || (lang === 'zh' ? '生成失败，请稍后重试。' : 'Generation failed, please try again later.');
        setApiError(errorMsg);
        // 将对应记录标记为失败
        if (recordId && meta) {
          setGeneratedResults(prev =>
            prev.map(r =>
              r.id === recordId
                ? {
                    ...r,
                    status: 'failed',
                    error: errorMsg,
                  }
                : r
            )
          );
        }
        setIsGenerating(false);
        setTaskId(null);
        if (pollingIntervalRef.current) {
          clearTimeout(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (e: unknown) {
      console.error('轮询任务状态失败:', e);
      const errorMessage = e instanceof Error ? e.message : '未知错误';
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setApiError('网络连接失败，请检查后端服务是否运行');
      } else {
        setApiError(`查询任务状态失败: ${errorMessage}`);
      }
      setIsGenerating(false);
      setTaskId(null);
      setPendingRecordId(null);
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  // 提交生成任务
  const handleGenerate = async () => {
    if (activeTab !== 'gen') return;
    
    // 从当前模块的隔离 state 读取提示词
    const prompt = (
      genMode === 'text' ? textModePrompt :
      genMode === 'video' && videoTab === 'textCtrl' ? textCtrlPrompt :
      genMode === 'video' ? videoModePrompt :
      refModePrompt
    ).trim();
    const negativePrompt = (
      genMode === 'text' ? textModeNegativePrompt :
      genMode === 'video' && videoTab === 'textCtrl' ? textCtrlNegativePrompt :
      genMode === 'video' ? videoModeNegativePrompt :
      refModeNegativePrompt
    ).trim();
    
    // 根据 genMode 验证
    if ((genMode === 'text' || (genMode === 'video' && videoTab === 'textCtrl')) && !prompt) {
      setApiErrorFor(genMode === 'video' ? 'video_text' : 'text');
      setApiError(lang === 'zh' ? '请输入提示词' : 'Please enter a prompt');
      return;
    }
    if (genMode === 'video') {
      const needFile = videoTab === 'textCtrl' ? textCtrlVideoFile : videoModeVideoFile;
      if (!needFile) {
        setApiError(lang === 'zh' ? '请上传视频文件' : 'Please upload a video file');
        setApiErrorFor(videoTab === 'textCtrl' ? 'video_text' : 'video_plain');
        return;
      }
    }
    if (genMode === 'ref' && (!refModeVideoFile || !refModeAudioFile)) {
      setApiError(lang === 'zh' ? '请上传视频和参考音频文件' : 'Please upload both video and reference audio files');
      setApiErrorFor('ref');
      return;
    }
    
    // 仅当前模块的按钮置灰 3s，其他模块不受影响
    if (genMode === 'text') setTextCooldownRemaining(3);
    else if (genMode === 'video') setVideoCooldownRemaining(3);
    else setRefCooldownRemaining(3);
    setIsGenerating(true);
    setApiError(null);
    setApiErrorFor(null);
    setTaskStatus('submitting');
    setQueuePos(null);
    
    // 清理之前的轮询
    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    try {
      let res: Response;
      // 在参考音频模式下，支持主模型和 Transfer V2
      const frontendModelName = (genMode === 'ref' && config.model !== 'Transfer V2') ? PRIMARY_MODEL : config.model;
      const modelName = MODEL_BACKEND_MAPPING[frontendModelName] || frontendModelName;

      // 本次任务用于记录到历史中的元信息快照
      const currentMeta = {
        mode: genMode === 'video' ? (videoTab === 'textCtrl' ? 'video_text' : 'video_plain') : genMode,
        prompt,
        negativePrompt: negativePrompt || undefined,
        model: frontendModelName,
        durationMode: curGenCfg.durationMode,
        duration: curGenCfg.duration,
        count: curGenCfg.count,
      };

      // 先在右侧追加一个“创作中”的记录卡片（放在最上方）
      const tempRecordId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const pendingRecord: GenerationRecord = {
        id: tempRecordId,
        mode: currentMeta.mode as any,
        prompt: currentMeta.prompt,
        negativePrompt: currentMeta.negativePrompt,
        model: currentMeta.model,
        durationMode: currentMeta.durationMode,
        duration: currentMeta.duration,
        count: currentMeta.count,
        createdAt: new Date().toISOString(),
        urls: [],
        status: 'pending',
      };
      setGeneratedResults(prev => [pendingRecord, ...prev]);
      setPendingRecordId(tempRecordId);
      
      // 文生音效模式：使用表单数据（application/x-www-form-urlencoded）
      console.log('Submitting task with config:', config);
      console.log('Generation Mode:', genMode);

      if (genMode === 'text') {
        const params = new URLSearchParams();
        params.append('model', modelName);
        params.append('prompt', prompt);
        
        if (negativePrompt) {
          params.append('negative_prompt', negativePrompt);
        }
        
        // 时长（仅在非智能模式下传递）
        if (config.durationMode === 'seconds') {
          params.append('duration', String(config.duration));
        }
        
        // 生成数量
        if (config.count) {
          params.append('count', String(config.count));
        }
        
        console.log('Text mode params:', params.toString());

        res = await fetch(PROCESS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: params.toString(),
        });
      } else {
        // 视频生音效和参考音频生音效模式：使用 FormData（multipart/form-data）
        const buildMediaFormData = () => {
          const fd = new FormData();
        
          // 必填参数：模型名称
          fd.append('model', modelName);
        
          // 可选参数
          if (prompt) {
            fd.append('prompt', prompt);
          }
        
          if (negativePrompt) {
            fd.append('negative_prompt', negativePrompt);
          }
        
          // 时长（仅在非智能模式下传递）
          if (config.durationMode === 'seconds') {
            fd.append('duration', String(config.duration));
          }
        
          // 生成数量
          if (config.count) {
            fd.append('count', String(config.count));
          }
        
          // 文件上传（按模式使用对应素材）
          const videoToUpload = genMode === 'video' ? (videoTab === 'textCtrl' ? textCtrlVideoFile : videoModeVideoFile) : refModeVideoFile;
          const audioToUpload = genMode === 'ref' ? refModeAudioFile : null;
          if (videoToUpload) {
            fd.append('file', videoToUpload);
          }
          if (audioToUpload) {
            fd.append('reference_audio', audioToUpload);
          }
          return fd;
        };
        const formData = buildMediaFormData();
        
        // Log FormData entries for debugging
        const formDataEntries: Record<string, any> = {};
        formData.forEach((value, key) => {
            // File objects are not easily serializable, just log name/size
            if (value instanceof File) {
                formDataEntries[key] = `File: ${value.name} (${value.size} bytes)`;
            } else {
                formDataEntries[key] = value;
            }
        });
        console.log('FormData content:', formDataEntries);

        res = await fetch(PROCESS_API_URL, {
          method: 'POST',
          body: formData,
        });
      }
      
      if (!res.ok) {
        const errorText = await res.text().catch(() => `HTTP ${res.status}`);
        let errorMessage = '提交任务失败';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          if (errorText) errorMessage = errorText;
        }
        throw new Error(errorMessage);
      }
      
      const data = await res.json();
      console.log('Submit response data:', data); // DEBUG
      const receivedTaskId = data.taskId || data.task_id;
      
      if (receivedTaskId) {
        // 任务提交成功：仅当前模块下弹窗 2s，切换模块不显示
        setToastMessage(lang === 'zh' ? '任务提交成功，生成中' : 'Task submitted, generating');
        setToastForMode(genMode);
        setToastShow(true);
        window.setTimeout(() => {
          setToastShow(false);
          setToastForMode(null);
        }, 2000);

        setTaskId(receivedTaskId);
        setTaskStatus(data.status || 'pending');
        setQueuePos((data.queue_position ?? data.queue_pos) || null);
        pollTaskStatus(receivedTaskId, currentMeta, tempRecordId);
      } else {
        throw new Error(lang === 'zh' ? '未收到任务ID' : 'No task ID received');
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : (lang === 'zh' ? '未知错误' : 'Unknown error');
      let displayMessage = lang === 'zh' ? '生成失败' : 'Failed to generate';
      const attemptedBase = API_BASE_URL;
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        displayMessage = (lang === 'zh'
          ? `无法连接到服务器，请检查后端服务可达 (${attemptedBase})；错误详情: ${errorMessage}`
          : `Cannot connect to server, please check backend reachability (${attemptedBase}); details: ${errorMessage}`);
      } else if (errorMessage.includes('CORS')) {
        displayMessage = (lang === 'zh' ? 'CORS 错误：请检查后端服务器的 CORS 配置' : 'CORS error: please check backend CORS configuration');
      } else if (errorMessage.includes('HTTP')) {
        displayMessage = (lang === 'zh' ? `服务器错误: ${errorMessage}` : `Server error: ${errorMessage}`);
      } else {
        displayMessage = (lang === 'zh' ? `生成失败: ${errorMessage}` : `Generation failed: ${errorMessage}`);
      }
      
      setApiError(displayMessage);
      setGeneratedResults([]);
      setIsGenerating(false);
      if (genMode === 'text') setTextCooldownRemaining(0);
      else if (genMode === 'video') setVideoCooldownRemaining(0);
      else setRefCooldownRemaining(0);
      setTaskId(null);
      setTaskStatus(null);
      setQueuePos(null);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // 清理轮询
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      Object.values(audioRefs.current).forEach(audio => {
          const audioEl = audio as HTMLAudioElement;
          audioEl.pause();
          audioEl.src = ''; 
      });
      if (videoModeVideoPreviewUrl) URL.revokeObjectURL(videoModeVideoPreviewUrl);
      if (refModeVideoPreviewUrl) URL.revokeObjectURL(refModeVideoPreviewUrl);
      if (refModeAudioPreviewUrl) URL.revokeObjectURL(refModeAudioPreviewUrl);
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      if (audioPreviewUrl) URL.revokeObjectURL(audioPreviewUrl);
    };
  }, [videoModeVideoPreviewUrl, refModeVideoPreviewUrl, refModeAudioPreviewUrl, videoPreviewUrl, audioPreviewUrl]);

  // 当前生成模式下需要展示的历史记录（用于右侧 OUTPUT 面板）
  const visibleResults = generatedResults.filter(record => {
    if (genMode === 'video') {
      return record.mode === (videoTab === 'textCtrl' ? 'video_text' : 'video_plain');
    }
    return record.mode === genMode;
  });

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex flex-col font-sans text-gray-900 relative">
      {/* Category Banner */}
      {showEditTab && (
        <div className="bg-white px-2 sm:px-4 lg:px-8 py-2 shadow-sm z-10 mt-12">
          <div className="grid grid-cols-2 gap-1 sm:gap-4 w-full h-12 sm:h-16">
             {TABS.filter((tab) => showEditTab || tab.id !== 'edit').map((tab) => {
               const isActive = activeTab === tab.id;
               return (
                 <div
                   key={tab.id}
                   className={`
                     relative rounded-xl cursor-pointer border-2 transition-all duration-300 flex flex-col justify-center px-2 sm:px-6
                     ${isActive ? `${tab.activeBorder} ${tab.activeBg}` : 'border-transparent bg-gray-50 hover:bg-gray-100'}
                   `}
                   onClick={() => { trackEvent('switch_tab', { tab: tab.id }); setActiveTab(tab.id); }}
                 >
                    {isActive && (
                      <div className={`absolute inset-0 bg-gradient-to-br ${tab.gradient} opacity-50 rounded-xl pointer-events-none`}></div>
                    )}
                    <div className="relative z-10">
                      <div className="flex items-center space-x-1 sm:space-x-3 mb-0 sm:mb-1">
                         <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full flex-shrink-0 transition-colors ${isActive ? tab.activeDot : 'bg-gray-300'}`}></div>
                         <span className={`font-bold text-xs sm:text-lg truncate ${isActive ? 'text-gray-900' : 'text-gray-600'}`}>{tab.name}</span>
                      </div>
                      <p className={`text-xs pl-3 sm:pl-5 hidden sm:block ${isActive ? 'text-gray-600' : 'text-gray-400'} truncate`}>{tab.sub}</p>
                    </div>
                 </div>
               );
             })}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 bg-white flex flex-col overflow-hidden">
          {/* Model Selection Bar：左上角仅保留返回首页 */}
          <div className="h-10 border-b border-gray-100 flex items-center px-6 flex-shrink-0 bg-white">
             <button 
               onClick={onBack}
               className="flex items-center text-gray-600 hover:text-primary transition-colors group"
             >
               <div className="p-1.5 rounded-full bg-gray-100 group-hover:bg-blue-50 transition-colors mr-2">
                 <ArrowLeft size={14} />
               </div>
               <span className="font-medium text-sm">{t.experience.backHome}</span>
             </button>
          </div>

          {/* Central Workspace */}
          <div className="flex-1 flex flex-col items-center justify-center p-4 relative bg-[#FAFAFA] min-h-0 pb-20">
              
              <div className={`w-full flex flex-col items-center ${activeTab === 'gen' ? 'max-w-[1100px]' : 'max-w-[900px]'}`}>
              <h1 className="text-3xl md:text-4xl font-extrabold mb-2 text-gray-900 tracking-tight">
                {activeTab === 'edit' ? t.experience.editTitle : t.experience.genTitle}
              </h1>

              {/* Try Now center star note below title */}
              {isTryNowMode && activeTab === 'gen' && (
                <div className="mb-5 text-center">
                  <span className="inline-flex items-center gap-1 text-gray-700 text-base sm:text-lg">
                    {lang === 'zh' ? (
                      <>如果您觉得这个项目有用，请在 <a href="https://github.com/xiaomi-research/controlfoley" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-800">GitHub 项目页面</a>给我们点个 {' '}
                        <Star size={16} className="inline-block text-yellow-500 align-[-1px]" fill="currentColor" stroke="currentColor" strokeWidth={1.5} />
                        ！
                        </>
                    ) : (
                      <> If you find this project useful, consider giving us a {' '}  
                        <Star size={16} className="inline-block text-yellow-500 align-[-1px]" fill="currentColor" stroke="currentColor" strokeWidth={1.5} />
                        on <a href="https://github.com/xiaomi-research/controlfoley" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-800">GitHub project page</a> !
                      </>
                    )}
                  </span>
                </div>
              )}

                      {/* 生成四个模块切换：Text-to-Audio / Video-to-Audio / Text-Controlled Video-to-Audio / Audio-Controlled Video-to-Audio */}
              {activeTab === 'gen' && (
                <div className="w-full mb-6">
                  <div className="flex gap-1 justify-center flex-wrap">
                    <button
                      onClick={() => {
                        trackEvent('switch_gen_mode', { mode: 'text' });
                        setGenMode('text');
                        // Transfer V2 仅在 ref 模式可用，切换时重置为默认模型
                        if (config.model === 'Transfer V2') {
                          setConfig(prev => ({...prev, model: PRIMARY_MODEL}));
                        }
                      }}
                      className={`px-6 py-3 text-sm font-medium transition-all rounded-tl-lg rounded-bl-lg ${
                        genMode === 'text'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-[#FAFAFA] text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      Text-to-Audio
                    </button>
                    <button
                      onClick={() => {
                        trackEvent('switch_gen_mode', { mode: 'video_plain' });
                        setGenMode('video');
                        setVideoTab('plain');
                        if (config.model === 'Transfer V2') {
                          setConfig(prev => ({...prev, model: PRIMARY_MODEL}));
                        }
                      }}
                      className={`px-6 py-3 text-sm font-medium transition-all ${
                        genMode === 'video' && videoTab === 'plain'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-[#FAFAFA] text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      Video-to-Audio
                    </button>
                    <button
                      onClick={() => {
                        trackEvent('switch_gen_mode', { mode: 'video_text_ctrl' });
                        setGenMode('video');
                        setVideoTab('textCtrl');
                      }}
                      className={`px-6 py-3 text-sm font-medium transition-all ${
                        genMode === 'video' && videoTab === 'textCtrl'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-[#FAFAFA] text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      Text-Controlled Video-to-Audio
                    </button>
                    <button
                      onClick={() => { trackEvent('switch_gen_mode', { mode: 'ref_audio' }); setGenMode('ref'); }}
                      className={`px-6 py-3 text-sm font-medium transition-all rounded-tr-lg rounded-br-lg ${
                        genMode === 'ref'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-[#FAFAFA] text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      Audio-Controlled Video-to-Audio
                    </button>
                  </div>
                </div>
              )}
              
              {/* Mode descriptions under Try Now tabs */}
              {activeTab === 'gen' && (
                <div className="w-full mb-2">
                  {genMode === 'text' && (
                    <div className="mb-3">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                        <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与文本描述相匹配的音频' : 'Generate audio from text descriptions'}</p>
                      </div>
                    </div>
                  )}
                  {genMode === 'video' && videoTab === 'plain' && (
                    <div className="mb-3">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                        <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与视频内容相匹配的音频' : 'Generate audio that matches video content'}</p>
                      </div>
                    </div>
                  )}
                  {genMode === 'video' && videoTab === 'textCtrl' && (
                    <div className="mb-3">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                        <div className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与文本语义对齐、视频时间同步的音频' : 'Generate audio aligned with text prompts while staying synchronized with the video'}</div>
                      </div>
                      {/* <div className="mt-1 text-gray-600 text-sm sm:text-base">
                        {lang === 'zh'
                          ? 'L0-L2 表示文本-视频冲突逐步提升（L0 表示两者一致，L1 表示动作或对象不一致，L2 表示动作与对象均不一致）'
                          : 'L0-L2 indicate increasing text-video conflict (L0 aligned; L1 action or subject mismatch; L2 both action and subject mismatch)'}
                      </div> */}
                    </div>
                  )}
                  {genMode === 'ref' && (
                    <div className="mb-3">
                      <div className="inline-flex items-center gap-2">
                        <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full"></span>
                        <p className="font-semibold text-lg sm:text-xl text-gray-800">{lang === 'zh' ? '生成与参考音频音色一致、视频时间同步的音频' : 'Generate audio with timbre matching reference audio while staying synchronized with the video'}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Input Box - 音效生成为左右排版：左输入，右输出 */}
              <div className={activeTab === 'gen' ? 'w-full flex flex-col lg:flex-row gap-4 -mt-[10px]' : 'w-full'}>
                {/* Left Panel - Input Controls（高度被 ResizeObserver 同步到右侧看板） */}
                <div
                  ref={activeTab === 'gen' ? leftPanelRef : null}
                  className={
                    activeTab === 'gen'
                      ? `flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-4 z-20 ${
                          genMode === 'text' ? 'min-h-[360px]' : ''
                        }`
                      : 'w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-2 z-20'
                  }
                >
                  {activeTab === 'gen' ? (
                    <>
                      {/* Hidden File Inputs */}
                      <input
                        type="file"
                        ref={videoInputRef}
                        accept="video/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const targetMode: ModeKey = videoUploadTargetRef.current === 'video_plain' ? 'video_plain'
                            : videoUploadTargetRef.current === 'video_text' ? 'video_text' : 'ref';
                          const ok = await checkMediaDuration(file, targetMode);
                          if (!ok) { e.target.value = ''; return; }
                          const url = URL.createObjectURL(file);
                          if (videoUploadTargetRef.current === 'video_plain') {
                            if (videoModeVideoPreviewUrl) URL.revokeObjectURL(videoModeVideoPreviewUrl);
                            setVideoModeVideoFile(file);
                            setVideoModeVideoPreviewUrl(url);
                            setVideoModeVideoError(false);
                            checkTotalFileSize('video_plain', file, null);
                          } else if (videoUploadTargetRef.current === 'video_text') {
                            if (textCtrlVideoPreviewUrl) URL.revokeObjectURL(textCtrlVideoPreviewUrl);
                            setTextCtrlVideoFile(file);
                            setTextCtrlVideoPreviewUrl(url);
                            setTextCtrlVideoError(false);
                            checkTotalFileSize('video_text', file, null);
                          } else {
                            if (refModeVideoPreviewUrl) URL.revokeObjectURL(refModeVideoPreviewUrl);
                            setRefModeVideoFile(file);
                            setRefModeVideoPreviewUrl(url);
                            setRefModeVideoError(false);
                            checkTotalFileSize('ref', file, refModeAudioFile);
                          }
                          videoUploadTargetRef.current = null;
                          e.target.value = '';
                        }}
                      />
                      <input
                        type="file"
                        ref={audioInputRef}
                        accept="audio/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const ok = await checkMediaDuration(file, 'ref');
                            if (!ok) { e.target.value = ''; return; }
                            if (refModeAudioPreviewUrl) URL.revokeObjectURL(refModeAudioPreviewUrl);
                            setRefModeAudioFile(file);
                            setRefModeAudioPreviewUrl(URL.createObjectURL(file));
                            checkTotalFileSize('ref', refModeVideoFile, file);
                          }
                          e.target.value = '';
                        }}
                      />

                      {/* Video Upload Area (for video modes)：固定高度、白底，素材缩放到区域内 */}
                      {genMode === 'video' && (videoTab === 'plain' || videoTab === 'textCtrl') && (
                        <div className="mb-4">
                          <div className="text-xs text-gray-500 uppercase mb-1.5">{lang === 'zh' ? '视频素材' : 'VIDEO MATERIAL'}</div>
                          <p className="text-[11px] text-gray-400 mb-1.5">{lang === 'zh' ? '上传的音视频文件大小加起来不能超过 100MB' : 'Total size of uploaded audio/video files must not exceed 100MB'}</p>
                          <div 
                            onClick={() => {
                              if (!(videoTab === 'textCtrl' ? textCtrlVideoPreviewUrl : videoModeVideoPreviewUrl)) {
                                videoUploadTargetRef.current = videoTab === 'textCtrl' ? 'video_text' : 'video_plain';
                                videoInputRef.current?.click();
                              }
                            }}
                            className={`w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 transition-colors relative overflow-hidden bg-white ${videoTab==='textCtrl' ? 'border-blue-300 hover:border-blue-400' : 'border-gray-300 hover:border-blue-400'}` }
                          >
                            {(videoTab === 'textCtrl' ? textCtrlVideoPreviewUrl : videoModeVideoPreviewUrl) ? (
                              <>
                                <video 
                                  key={(videoTab === 'textCtrl' ? textCtrlVideoPreviewUrl : videoModeVideoPreviewUrl) || ''}
                                  src={(videoTab === 'textCtrl' ? textCtrlVideoPreviewUrl : videoModeVideoPreviewUrl) || ''}
                                  className="w-full h-full object-contain bg-white"
                                  muted
                                  loop
                                  autoPlay
                                  playsInline
                                  onLoadedData={(e) => {
                                    const video = e.currentTarget;
                                    video.play().catch(() => {});
                                  }}
                                  onError={() => (videoTab === 'textCtrl' ? setTextCtrlVideoError(true) : setVideoModeVideoError(true))}
                                />
                                {(videoTab === 'textCtrl' ? textCtrlVideoError : videoModeVideoError) && (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 p-3 z-[5]">
                                    <span className="text-xs text-amber-700 text-center">{lang === 'zh' ? '该视频格式无法预览，请使用 H.264 编码的 MP4 后重新上传' : 'This video format cannot be previewed. Please use H.264 encoded MP4 and re-upload'}</span>
                                  </div>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (videoTab === 'textCtrl') {
                                      if (textCtrlVideoPreviewUrl) URL.revokeObjectURL(textCtrlVideoPreviewUrl);
                                      setTextCtrlVideoFile(null);
                                      setTextCtrlVideoPreviewUrl(null);
                                      setTextCtrlVideoError(false);
                                      checkTotalFileSize('video_text', null, null);
                                    } else {
                                      if (videoModeVideoPreviewUrl) URL.revokeObjectURL(videoModeVideoPreviewUrl);
                                      setVideoModeVideoFile(null);
                                      setVideoModeVideoPreviewUrl(null);
                                      setVideoModeVideoError(false);
                                      checkTotalFileSize('video_plain', null, null);
                                    }
                                  }}
                                  className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center z-10"
                                  title={lang === 'zh' ? '删除' : 'Delete'}
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <Video size={24} className="text-gray-400 mb-1.5" />
                                <span className="text-xs text-gray-500">{t.experience.config.uploadVideoClick}</span>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Reference Audio Upload Area (for ref mode)：仅显示本模式上传的视频/音频，与视频生音效互不显示 */}
                      {genMode === 'ref' && (
                        <div className="mb-4">
                          <input
                            type="file"
                            ref={refModeVideoInputRef}
                            accept="video/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const ok = await checkMediaDuration(file, 'ref');
                              if (!ok) { e.target.value = ''; return; }
                              const url = URL.createObjectURL(file);
                              if (refModeVideoPreviewUrl) URL.revokeObjectURL(refModeVideoPreviewUrl);
                              setRefModeVideoFile(file);
                              setRefModeVideoPreviewUrl(url);
                              setRefModeVideoError(false);
                              checkTotalFileSize('ref', file, refModeAudioFile);
                              e.target.value = '';
                            }}
                          />
                          <div className="text-xs text-gray-500 uppercase mb-1.5">{lang === 'zh' ? '视频素材&参考音频' : 'VIDEO MATERIAL & REFERENCE AUDIO'}</div>
                          <p className="text-[11px] text-gray-400 mb-1.5">{lang === 'zh' ? '上传的音视频文件大小加起来不能超过 100MB' : 'Total size of uploaded audio/video files must not exceed 100MB'}</p>
                          <div className="flex gap-3">
                            {/* Video Upload - 与右侧音频区域同高、同风格，空白时为白底 */}
                            <div className="flex-1">
                              <div 
                                onClick={() => !refModeVideoPreviewUrl && refModeVideoInputRef.current?.click()}
                                className={`w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 transition-colors relative overflow-hidden bg-white ${videoTab==='textCtrl' ? 'border-blue-300 hover:border-blue-400' : 'border-gray-300 hover:border-blue-400'}` }
                              >
                                {refModeVideoPreviewUrl ? (
                                  <>
                                    <video 
                                      key={refModeVideoPreviewUrl}
                                      src={refModeVideoPreviewUrl}
                                      className="w-full h-full object-contain bg-white"
                                      muted
                                      loop
                                      autoPlay
                                      playsInline
                                      onLoadedData={(e) => {
                                        const video = e.currentTarget;
                                        video.play().catch(() => {});
                                      }}
                                      onError={() => setRefModeVideoError(true)}
                                    />
                                    {refModeVideoError && (
                                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 p-3 z-[5]">
                                        <span className="text-xs text-amber-700 text-center">{lang === 'zh' ? '该视频格式无法预览，请使用 H.264 编码的 MP4 后重新上传' : 'This video format cannot be previewed. Please use H.264 encoded MP4 and re-upload'}</span>
                                      </div>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (refModeVideoPreviewUrl) {
                                          URL.revokeObjectURL(refModeVideoPreviewUrl);
                                        }
                                        setRefModeVideoFile(null);
                                        setRefModeVideoPreviewUrl(null);
                                        setRefModeVideoError(false);
                                        checkTotalFileSize('ref', null, refModeAudioFile);
                                      }}
                                      className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center z-10"
                                      title={lang === 'zh' ? '删除' : 'Delete'}
                                    >
                                      <X size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <Video size={24} className="text-gray-400 mb-1.5" />
                                    <span className="text-xs text-gray-500">{t.experience.config.uploadVideoClick}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {/* Audio Upload - 与左侧视频区域同高 */}
                            <div className="flex-1">
                              <div 
                                onClick={() => !refModeAudioPreviewUrl && audioInputRef.current?.click()}
                                className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors relative bg-white"
                              >
                                {refModeAudioPreviewUrl ? (
                                  <>
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100 p-3">
                                      <audio 
                                        src={refModeAudioPreviewUrl}
                                        controls
                                        className="w-full max-w-md"
                                      />
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (refModeAudioPreviewUrl) {
                                          URL.revokeObjectURL(refModeAudioPreviewUrl);
                                        }
                                        setRefModeAudioFile(null);
                                        setRefModeAudioPreviewUrl(null);
                                        checkTotalFileSize('ref', refModeVideoFile, null);
                                      }}
                                      className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center z-10"
                                      title={lang === 'zh' ? '删除' : 'Delete'}
                                    >
                                      <X size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <Music size={24} className="text-gray-400 mb-1.5" />
                                    <span className="text-xs text-gray-500">{t.experience.config.uploadRefAudioClick}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 文件大小超限警告 */}
                      {fileSizeWarning[currentGenKey] && (
                        <div className="mb-4 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                          {fileSizeWarning[currentGenKey]}
                        </div>
                      )}

                      {/* Creative Prompt Section - 按模块隔离（Text-Controlled Video-to-Audio 强制填写） */}
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 uppercase mb-1.5">{lang === 'zh' ? '提示' : 'Prompt'}</div>
                        <textarea
                          value={genMode === 'text' ? textModePrompt : genMode === 'video' && videoTab === 'textCtrl' ? textCtrlPrompt : genMode === 'video' ? videoModePrompt : refModePrompt}
                          onChange={(e) => {
                            if (genMode === 'text') setTextModePrompt(e.target.value);
                            else if (genMode === 'video' && videoTab === 'textCtrl') setTextCtrlPrompt(e.target.value);
                            else if (genMode === 'video') setVideoModePrompt(e.target.value);
                            else setRefModePrompt(e.target.value);
                          }}
                          placeholder={genMode === 'video' && videoTab === 'textCtrl'
                            ? (lang === 'zh' ? '（必填）请输入控制视频音效生成的文本提示' : '(Required)Enter a text prompt to control video-to-audio')
                            : genMode === 'video'
                            ? (lang === 'zh' ? '（可选）请输入音效创意描述（建议使用英文）' : '(Optional) Enter sound design prompt')
                            : genMode === 'ref'
                            ? (lang === 'zh' ? '（可选）请输入音效创意描述（建议使用英文）' : '(Optional) Enter sound design prompt')
                            : (lang === 'zh' ? '请输入音效创意描述（建议使用英文）' : 'Enter sound design prompt')}
                          className={`w-full p-3 border border-gray-200 rounded-lg resize-none text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            genMode === 'text' ? 'min-h-[160px]' : 'min-h-[80px]'
                          }`}
                        />
                      </div>

                      {/* Negative Terms Section - 按模块隔离 */}
                      <div className="mb-4">
                        <div className="text-xs text-gray-500 uppercase mb-1.5">{lang === 'zh' ? '负向提示' : 'Negative prompt'}</div>
                        <textarea
                          value={genMode === 'text' ? textModeNegativePrompt : genMode === 'video' && videoTab === 'textCtrl' ? textCtrlNegativePrompt : genMode === 'video' ? videoModeNegativePrompt : refModeNegativePrompt}
                          onChange={(e) => {
                            if (genMode === 'text') setTextModeNegativePrompt(e.target.value);
                            else if (genMode === 'video' && videoTab === 'textCtrl') setTextCtrlNegativePrompt(e.target.value);
                            else if (genMode === 'video') setVideoModeNegativePrompt(e.target.value);
                            else setRefModeNegativePrompt(e.target.value);
                          }}
                          placeholder={lang === 'zh' ? '（可选）请输入音效中不希望存在的元素（建议使用英文）' : '(Optional) Enter unwanted audio elements'}
                          className={`w-full p-3 border border-gray-200 rounded-lg resize-none text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            genMode === 'text' ? 'min-h-[160px]' : 'min-h-[80px]'
                          }`}
                        />
                      </div>

                      {/* Bottom Controls */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="relative" ref={configRef}>
                            <button 
                              onClick={() => setShowConfig(!showConfig)}
                              className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <SlidersHorizontal size={14} />
                              <span className="flex items-center gap-2">
                                <span>{t.experience.config.modelLabel} {config.model}</span>
                                <span className="text-gray-300">|</span>
                                <span>{t.experience.config.durationLabel} {config.durationMode === 'smart' ? t.experience.config.smartShort : `${config.duration}${t.experience.config.sec}`}</span>
                                <span className="text-gray-300">|</span>
                                <span>{t.experience.config.countLabel} {config.count}{t.experience.config.item}</span>
                              </span>
                            </button>

                            {/* SETTINGS POPUP */}
                            {showConfig && (
                              <div className="absolute top-full left-0 mt-2 w-[360px] bg-white rounded-xl shadow-2xl border border-gray-100 p-5 z-30 animate-in fade-in zoom-in-95 duration-200 origin-top-left text-left">
                                {/* Model Selection */}
                                {false && (                                                                                                                          
                                  <div className="mb-5">                
                                    <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.modelSelect}</div>
                                    {modelsLoadError && (
                                      <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                                        {modelsLoadError}，使用默认模型列表
                                      </div>
                                    )}
                                    <div className="flex bg-gray-100 p-1 rounded-lg">
                                      {MODELS.map(model => {
                                        const isBackendAvailable = availableModels.includes(model);
                                        
                                        // 模式兼容性检查
                                        const isRefMode = genMode === 'ref';
                                        let isModeCompatible = true;
                                        
                                        if (isRefMode) {
                                        // ref 模式：仅支持主模型和 Transfer V2
                                        if (model !== PRIMARY_MODEL && model !== 'Transfer V2') {
                                            isModeCompatible = false;
                                          }
                                        } else {
                                          // text/video 模式：不支持 'Transfer V2'
                                          if (model === 'Transfer V2') {
                                            isModeCompatible = false;
                                          }
                                        }
                                        
                                        const isSelectable = isBackendAvailable && isModeCompatible;

                                        return (
                                          <button
                                            key={model}
                                            onClick={() => isSelectable && setConfig({...config, model: model})}
                                            disabled={!isSelectable}
                                            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                              config.model === model
                                                ? 'bg-white text-gray-900 shadow-sm' 
                                                : isSelectable 
                                                  ? 'text-gray-500 hover:text-gray-700'
                                                  : 'text-gray-300 cursor-not-allowed opacity-60'
                                            }`}
                                            title={!isBackendAvailable ? "模型暂不可用" : (!isModeCompatible ? "该模型不支持当前模式" : "")}
                                          >
                                            {model}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Duration */}
                                <div className="mb-5">
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.durationGen}</div>
                                  <div className="flex bg-gray-100 p-1 rounded-lg mb-4 w-full">
                                    <button 
                                      onClick={() => setConfig({...config, durationMode: 'seconds'})}
                                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${config.durationMode === 'seconds' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                    >
                                      {t.experience.config.bySeconds}
                                    </button>
                                    <button 
                                       onClick={() => setConfig({...config, durationMode: 'smart'})}
                                       className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${config.durationMode === 'smart' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                    >
                                      {t.experience.config.smartDuration}
                                    </button>
                                  </div>
                                  
                                  <div className={`flex items-center gap-4 transition-opacity ${config.durationMode === 'smart' ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                                    <input 
                                      type="range" 
                                      min="1" 
                                      max="30" 
                                      step="1"
                                      value={config.duration}
                                      onChange={(e) => setConfig({...config, duration: parseInt(e.target.value)})}
                                      className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <div className="w-16 h-8 flex items-center justify-center border border-gray-200 rounded-md bg-white text-sm font-medium text-gray-700 shadow-sm">
                                      {config.duration} {t.experience.config.sec}
                                    </div>
                                  </div>
                                </div>

                                {/* Count */}
                                <div>
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.countSelect}</div>
                                  <div className="flex items-center gap-4">
                                    <input 
                                      type="range" 
                                      min="1" 
                                      max="4" 
                                      step="1"
                                      value={config.count}
                                      onChange={(e) => setConfig({...config, count: parseInt(e.target.value)})}
                                      className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <div className="w-16 h-8 flex items-center justify-center border border-gray-200 rounded-md bg-white text-sm font-medium text-gray-700 shadow-sm">
                                      {config.count}{t.experience.config.item && ` ${t.experience.config.item}`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Generate Button：蓝色主按钮，当前模块独立冷却 3s */}
                        <button
                          onClick={() => { trackEvent('click_generate', { mode: genMode }); handleGenerate(); }}
                          disabled={(genMode === 'text' ? textCooldownRemaining : genMode === 'video' ? videoCooldownRemaining : refCooldownRemaining) > 0}
                          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                        >
                          <span>{lang === 'zh' ? '生成音频' : 'Generate Audio'}</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    // Edit tab layout
                    <>
                      {/* Top Input Area */}
                      <div className="flex p-3 gap-3">
                        {/* Upload Boxes */}
                        <div className="flex flex-col gap-2">
                          {/* Hidden File Inputs */}
                          <input
                            type="file"
                            ref={videoInputRef}
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setVideoFile(file);
                                const url = URL.createObjectURL(file);
                                setVideoPreviewUrl(url);
                              }
                            }}
                          />
                          <input
                            type="file"
                            ref={audioInputRef}
                            accept={activeTab === 'edit' ? "video/*" : "audio/*"}
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setAudioFile(file);
                                const url = URL.createObjectURL(file);
                                setAudioPreviewUrl(url);
                              }
                            }}
                          />
                          {/* 编辑 tab 下的视频输入 */}
                          {activeTab === 'edit' && (
                            <div 
                              onClick={() => !audioPreviewUrl && !audioFile && audioInputRef.current?.click()}
                              className={`w-16 h-16 bg-gray-50 border border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-blue-50 transition-colors group relative ${
                                audioFile ? 'border-primary bg-blue-50' : 'border-gray-300'
                              }`}
                            >
                              <Video size={16} className={`${audioFile ? 'text-primary' : 'text-gray-400'} group-hover:text-primary mb-0.5`} />
                              <span className={`text-[9px] ${audioFile ? 'text-primary' : 'text-gray-400'} group-hover:text-primary`}>{lang === 'zh' ? '视频输入' : 'Video Input'}</span>
                              {audioFile && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (audioPreviewUrl) {
                                      URL.revokeObjectURL(audioPreviewUrl);
                                    }
                                    setAudioFile(null);
                                    setAudioPreviewUrl(null);
                                  }}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-primary hover:bg-red-500 text-white rounded-full flex items-center justify-center z-10"
                                  title={lang === 'zh' ? '删除' : 'Delete'}
                                >
                                  <X size={10} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Text Input */}
                        <div className="flex-1 flex flex-col gap-2">
                          <div className="flex-1 min-h-[120px]">
                            <textarea 
                              ref={positivePromptRef}
                              placeholder={lang === 'zh' ? '先选择编辑功能，之后输入你想进行的操作，例如：提取-背景音乐、删除-噪音等，支持文字和视觉交互' : 'Select an editing function first, then enter the operation you want to perform, e.g. Extract - Background music, Delete - Noise, etc. Supports text and visual interaction'}
                              className="w-full h-full resize-none text-gray-700 placeholder-gray-400 bg-transparent outline-none text-sm pt-1"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="h-px bg-gray-100 mx-4"></div>

                      {/* Bottom Toolbar */}
                      <div className="flex items-center justify-between p-2">
                        <div className="flex items-center gap-3">
                          {/* Config Settings Button */}
                          <div className="relative" ref={configRef}>
                            <button 
                              onClick={() => setShowConfig(!showConfig)}
                              className={`flex items-center gap-2 border rounded-md px-3 py-1.5 text-xs transition-all shadow-sm ${
                                showConfig ? 'border-primary bg-blue-50 text-primary ring-2 ring-blue-100' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <SlidersHorizontal size={14} />
                              <span className="flex items-center">
                                <span>{config.editMode === 'extract' ? (lang === 'zh' ? '提取' : 'Extract') : config.editMode === 'delete' ? (lang === 'zh' ? '删除' : 'Delete') : config.editMode === 'replace' ? (lang === 'zh' ? '替换' : 'Replace') : (lang === 'zh' ? '增加' : 'Add')}</span>
                                <span className="border-l border-gray-300 h-3 mx-2"></span>
                                <span>{config.model}</span>
                                <span className="border-l border-gray-300 h-3 mx-2"></span>
                                <span>{config.durationMode === 'smart' ? t.experience.config.smartDuration : `${config.duration}${t.experience.config.sec}`}</span>
                                <span className="border-l border-gray-300 h-3 mx-2"></span>
                                <span>{config.count}{t.experience.config.item && ` ${t.experience.config.item}`}</span>
                              </span>
                            </button>

                            {/* SETTINGS POPUP - 仅编辑页使用 */}
                            {showConfig && (
                              <div className="absolute top-full left-0 mt-3 w-[360px] bg-white rounded-xl shadow-2xl border border-gray-100 p-5 z-30 animate-in fade-in zoom-in-95 duration-200 origin-top-left text-left">
                                <div className="mb-5">
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.editOptions}</div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <button
                                        onClick={() => setConfig({...config, editMode: 'extract'})}
                                        className={`py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                                          config.editMode === 'extract'
                                            ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                                        }`}
                                      >
                                        {lang === 'zh' ? '提取' : 'Extract'}
                                      </button>
                                      <button
                                        onClick={() => setConfig({...config, editMode: 'delete'})}
                                        className={`py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                                          config.editMode === 'delete'
                                            ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                                        }`}
                                      >
                                        {lang === 'zh' ? '删除' : 'Delete'}
                                      </button>
                                      <button
                                        onClick={() => setConfig({...config, editMode: 'replace'})}
                                        className={`py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                                          config.editMode === 'replace'
                                            ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                                        }`}
                                      >
                                        {lang === 'zh' ? '替换' : 'Replace'}
                                      </button>
                                      <button
                                        onClick={() => setConfig({...config, editMode: 'add'})}
                                        className={`py-2 px-3 text-xs font-medium rounded-lg transition-all ${
                                          config.editMode === 'add'
                                            ? 'bg-purple-100 text-purple-700 border-2 border-purple-500'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                                        }`}
                                      >
                                        {lang === 'zh' ? '增加' : 'Add'}
                                      </button>
                                    </div>
                                </div>

                                {/* Model Selection */}
                                <div className="mb-5">
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.modelSelect}</div>
                                  <div className="flex bg-gray-100 p-1 rounded-lg">
                                    {MODELS.map(model => (
                                      <button
                                        key={model}
                                        onClick={() => setConfig({...config, model: model})}
                                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                          config.model === model
                                            ? 'bg-white text-gray-900 shadow-sm' 
                                            : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                      >
                                        {model}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* 2. Duration */}
                                <div className="mb-5">
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.durationGen}</div>
                                  <div className="flex bg-gray-100 p-1 rounded-lg mb-4 w-full">
                                    <button 
                                      onClick={() => setConfig({...config, durationMode: 'seconds'})}
                                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${config.durationMode === 'seconds' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                    >
                                      {t.experience.config.bySeconds}
                                    </button>
                                    <button 
                                       onClick={() => setConfig({...config, durationMode: 'smart'})}
                                       className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${config.durationMode === 'smart' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                    >
                                      {t.experience.config.smartDuration}
                                    </button>
                                  </div>
                                  
                                  <div className={`flex items-center gap-4 transition-opacity ${config.durationMode === 'smart' ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                                    <input 
                                      type="range" 
                                      min="1" 
                                      max="30" 
                                      step="1"
                                      value={config.duration}
                                      onChange={(e) => setConfig({...config, duration: parseInt(e.target.value)})}
                                      className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <div className="w-16 h-8 flex items-center justify-center border border-gray-200 rounded-md bg-white text-sm font-medium text-gray-700 shadow-sm">
                                      {config.duration} {t.experience.config.sec}
                                    </div>
                                  </div>
                                </div>

                                {/* 3. Count */}
                                <div>
                                  <div className="text-sm font-medium text-gray-700 mb-3">{t.experience.config.countSelect}</div>
                                  <div className="flex items-center gap-4">
                                    <input 
                                      type="range" 
                                      min="1" 
                                      max="4" 
                                      step="1"
                                      value={config.count}
                                      onChange={(e) => setConfig({...config, count: parseInt(e.target.value)})}
                                      className="flex-1 h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                    />
                                    <div className="w-16 h-8 flex items-center justify-center border border-gray-200 rounded-md bg-white text-sm font-medium text-gray-700 shadow-sm">
                                      {config.count}{t.experience.config.item && ` ${t.experience.config.item}`}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Submit Button - 其他 tab：圆形箭头 */}
                        <button className="w-8 h-8 bg-[#b0aaff] hover:bg-[#9f96fc] rounded-full flex items-center justify-center transition-colors shadow-md">
                          <ArrowUp size={16} className="text-white" />
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* 音效生成：右侧输出区域 - 高度与左侧看板同步，超出部分内部滚动 */}
                {activeTab === 'gen' && (
                  <div
                    className="flex-1 min-h-0 overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm p-4 z-20 flex flex-col"
                    style={{ maxHeight: leftPanelHeight != null ? leftPanelHeight : 520 }}
                  >
                    <div className="text-xs text-gray-400 uppercase mb-3 flex-shrink-0">{lang === 'zh' ? '生成结果' : 'Generated Results'}</div>
                    <div className={`flex-1 flex flex-col min-h-0 ${visibleResults.length > 0 ? 'text-left' : 'items-center justify-center text-center'}`}>
                      {apiError && ((genMode === 'video' ? (videoTab === 'textCtrl' ? 'video_text' : 'video_plain') : genMode) === apiErrorFor) && (
                        <div className="w-full">
                          <p className="text-red-500 text-sm mb-2">{apiError}</p>
                        </div>
                      )}
                      
                      {/* 空状态：当前模式下没有历史记录时展示 */}
                      {!isGenerating && visibleResults.length === 0 && (!apiError || ((genMode === 'video' ? (videoTab === 'textCtrl' ? 'video_text' : 'video_plain') : genMode) !== apiErrorFor)) && (
                        <>
                          <Music size={48} className="text-gray-300 mb-3" />
                          <p className="text-gray-400 text-sm">{lang === 'zh' ? '快来试试吧，在左侧输入音效描述' : 'Enter a sound description on the left to get started'}</p>
                        </>
                      )}
                      
                      {/* 生成结果：填满可用高度，内部滚动；只展示当前模式的记录 */}
                      {visibleResults.length > 0 && (
                        <div className="w-full flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
                          {visibleResults.map((record) => (
                            <div
                              key={record.id}
                              className={`w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm flex flex-col justify-between ${
                                genMode === 'text' ? 'min-h-32' : 'min-h-28'
                              }`}
                            >
                              {/* 头部：模式 + 状态 + 时间 + 删除 */}
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-700">
                                    {record.mode === 'text'
                                      ? (lang === 'zh' ? '文本生音效' : 'Text-to-Sound')
                                      : record.mode === 'video_text'
                                      ? (lang === 'zh' ? '文本控制生音效' : 'Text-Controlled Video-to-Sound')
                                      : record.mode === 'video_plain' || record.mode === 'video'
                                      ? (lang === 'zh' ? '视频生音效' : 'Video-to-Sound')
                                      : record.mode === 'ref'
                                      ? (lang === 'zh' ? '音频控制生音效' : 'Audio-Controlled Video-to-Sound')
                                      : (lang === 'zh' ? '视频生音效' : 'Video-to-Sound')}
                                  </span>
                                  <span className="text-[11px] text-gray-400">
                                    {lang === 'zh' ? '模型' : 'Model'}：{record.model} · {lang === 'zh' ? '时长' : 'Duration'}：
                                    {record.durationMode === 'smart'
                                      ? (lang === 'zh' ? '智能' : 'Smart')
                                      : `${record.duration}${lang === 'zh' ? '秒' : 's'}`} · {lang === 'zh' ? '数量' : 'Count'}：{record.count}
                                  </span>
                                  <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                                    {record.status === 'pending'
                                      ? (taskStatus === 'submitting'
                                          ? (lang === 'zh' ? '提交任务中' : 'Submitting task')
                                          : taskStatus === 'pending' && queuePos !== null
                                            ? (lang === 'zh' ? `排队中 (${queuePos})` : `In queue (${queuePos})`)
                                            : taskStatus === 'processing'
                                              ? (lang === 'zh' ? '正在生成' : 'Generating')
                                              : (lang === 'zh' ? '等待结果' : 'Waiting for result'))
                                      : record.status === 'failed'
                                        ? (lang === 'zh' ? '生成失败' : 'Failed')
                                        : (lang === 'zh' ? '生成完成' : 'Completed')}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-gray-400">
                                    {new Date(record.createdAt).toLocaleTimeString()}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setGeneratedResults(prev => prev.filter(r => r.id !== record.id))}
                                    className="w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                    title={lang === 'zh' ? '删除此记录' : 'Delete this record'}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                                    </svg>
                                  </button>
                                </div>
                              </div>

                              {/* 文本提示 */}
                              {record.prompt && (
                                <p className="text-xs text-gray-700 mb-1 line-clamp-2">
                                  {lang === 'zh' ? '提示' : 'Prompt'}: {record.prompt}
                                </p>
                              )}
                              {record.negativePrompt && (
                                <p className="text-xs text-gray-500 mb-1 line-clamp-2">
                                  {lang === 'zh' ? '负向提示' : 'Negative prompt'}: {record.negativePrompt}
                                </p>
                              )}

                              {/* 实际生成的音频 / 视频结果列表或占位状态 */}
                              <div className="mt-1 space-y-1">
                                {record.status === 'pending' && record.urls.length === 0 && (
                                  <div className="w-full h-10 rounded-md bg-gray-100 flex items-center justify-between px-3 text-xs text-gray-500">
                                    <span>
                                      {taskStatus === 'submitting'
                                        ? (lang === 'zh' ? '正在提交任务...' : 'Submitting task...')
                                        : taskStatus === 'pending' && queuePos !== null
                                          ? (lang === 'zh' ? `排队中，前面还有 ${queuePos} 个任务...` : `In queue, ${queuePos} ahead...`)
                                          : taskStatus === 'processing'
                                            ? (lang === 'zh' ? '模型正在生成中，请稍候...' : 'Model is generating, please wait...')
                                            : (lang === 'zh' ? '等待模型返回结果...' : 'Waiting for model results...')}
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                                      <span>{lang === 'zh' ? '创作中' : 'Creating'}</span>
                                    </div>
                                  </div>
                                )}

                                {record.status === 'failed' && record.error && (
                                  <div className="w-full rounded-md bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-600">
                                    {record.error}
                                  </div>
                                )}

                                {record.urls.length > 0 && (
                                  <>
                                    {record.mode === 'video' ? (
                                      /* 视频生音效：只显示视频结果，数量与请求条数一致（如生成4条显示4条），用 1、2、3、4 按钮切换 */
                                      (() => {
                                        const videoUrls = record.urls
                                          .filter((u: string) => /\.(mp4|webm|mov)$/i.test(u))
                                          .slice(0, record.count);
                                        if (videoUrls.length === 0) return null;
                                        const idx = Math.min(selectedVideoResultIndex[record.id] ?? 0, videoUrls.length - 1);
                                        const url = videoUrls[idx];
                                        return (
                                          <div className="space-y-2">
                                            <div className="w-full rounded-md bg-black overflow-hidden">
                                              <video
                                                key={url}
                                                src={url}
                                                controls
                                                className="w-full h-full object-contain"
                                              />
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 justify-center">
                                              {videoUrls.map((_, i) => (
                                                <button
                                                  key={i}
                                                  type="button"
                                                  onClick={() => setSelectedVideoResultIndex(prev => ({ ...prev, [record.id]: i }))}
                                                  className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                                                    i === idx
                                                      ? 'bg-blue-600 text-white'
                                                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                                  }`}
                                                >
                                                  {i + 1}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })()
                                    ) : (
                                      /* 文生音效 / 参考音频：顺序显示全部结果 */
                                      record.urls.map((url, idx) =>
                                        /\.(mp4|webm|mov)$/i.test(url) ? (
                                          <video key={idx} src={url} controls className="w-full rounded-md bg-black" />
                                        ) : (
                                          <audio key={idx} src={url} controls className="w-full" />
                                        )
                                      )
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Bottom Helpers */}
                <>
                  <div className="w-full mt-4 flex justify-between items-center text-sm text-gray-500">
                  {/* {!isTryNowMode && (                                                                                                                                       
                    <div                                                                                                                                             
                      className="flex items-center gap-2 cursor-pointer hover:text-gray-800 transition-colors"                                                              
                      onClick={() => setShowExamples(!showExamples)}                                                                                                 
                    >                                                                                                                                                
                      试试以下示例 <ChevronDown size={14} className={`transition-transform duration-300 ${showExamples ? 'rotate-180' : ''}`} />                     
                    </div>                                                                                                                                           
                  )}  */}
                    <div
                      className="flex items-center gap-2 cursor-pointer text-primary font-semibold bg-blue-50 px-2.5 py-1.5 rounded-md border border-blue-200 hover:bg-blue-100 transition-colors shadow-sm"
                      onClick={() => setShowPromptGuide(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowPromptGuide(true);
                        }
                      }}
                    >
                      <Lightbulb size={14} />
                      {lang === 'zh' ? '提示词指南' : 'Prompt Guide'}
                    </div>
                    {!isTryNowMode && activeTab === 'gen' && (
                      <span className="hidden sm:block text-gray-600">
                        {lang === 'zh' ? (                                                                                                                                                                
                           <>如果你觉得这个项目不错，可以访问我们的 <a href="https://github.com/xiaomi-research/controlfoley" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-800">GitHub 主页</a> 点个 star{' '}                                                                                          
                          <Star size={12} className="inline-block text-yellow-500 align-[-1px]" fill="currentColor" stroke="currentColor" strokeWidth={1.5} />                                          
                          ，谢谢！</>                                                                                                                                                                   
                    ) : (                                                                                                                                                                             
                      <>If you like this project, please visit our <a href="https://github.com/xiaomi-research/controlfoley" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-800">GitHub project page </a> and give it a star{' '}                                                                                  
                        <Star size={12} className="inline-block text-yellow-500 align-[-1px]" fill="currentColor" stroke="currentColor" strokeWidth={1.5} />                                          
                        , thanks!</>                                                                                                                                                                  
                        )}
                      </span>
                    )}
                  </div>

                  {/* Try Now inline demos under Text-to-Audio */}
                  {isTryNowMode && activeTab === 'gen' && genMode === 'text' && (
                    <div className="w-full mt-4">
                      <div className="flex items-center justify-center gap-2 text-base sm:text-lg font-semibold text-gray-800 mb-2"><Mic size={16} className="text-primary" /><span>{t.experience.tryDemos.textTitle}</span></div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {TEXT_TRY_DEMOS.map(demo => (
                          <div key={demo.id} className="p-3 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all">
                            <div className="text-sm sm:text-base font-medium text-gray-900 mb-2 truncate" title={demo.title}>{demo.title}</div>
                            <audio src={demo.url} controls className="w-full block" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Try Now inline demos under Video-to-Audio */}
                  {isTryNowMode && activeTab === 'gen' && genMode === 'video' && videoTab === 'plain' && (
                    <div className="w-full mt-4">
                      <div className="flex items-center justify-center gap-2 text-base sm:text-lg font-semibold text-gray-800 mb-2"><Video size={16} className="text-primary" /><span>{t.experience.tryDemos.videoTitle}</span></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {VIDEO_TRY_DEMOS.map(demo => (
                          <div key={demo.id} className="p-2 bg-white rounded-lg border border-gray-200 flex flex-col">
                            <LazyVideo src={demo.url} className="w-full aspect-video object-contain bg-black" controls playsInline crossOrigin="anonymous" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Try Now inline demos under Text-Controlled Video-to-Audio */}
                  {isTryNowMode && activeTab === 'gen' && genMode === 'video' && videoTab === 'textCtrl' && (
                    <div className="w-full mt-4">
                      <div className="flex items-center justify-center gap-2 text-base sm:text-lg font-semibold text-gray-800 mb-2"><Type size={16} className="text-primary" /><span>{t.experience.tryDemos.textCtrlTitle}</span></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TEXTCTRL_TRY_DEMOS.map(demo => (
                          <div key={demo.id} className="p-2 bg-white rounded-lg border border-gray-200 flex flex-col">
                            <LazyVideo src={demo.url} className="w-full aspect-video object-contain bg-black" controls playsInline crossOrigin="anonymous" />
                            <div className="mt-2 p-2 rounded-md border border-gray-200 bg-white w-full text-sm sm:text-base text-gray-800"><span className="mr-1">{t.experience.tryDemos.promptLabel}</span><strong>{demo.prompt}</strong></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Try Now inline demos under Audio-Controlled Video-to-Audio */}
                  {isTryNowMode && activeTab === 'gen' && genMode === 'ref' && (
                    <div className="w-full mt-4">
                      <div className="flex items-center justify-center gap-2 text-base sm:text-lg font-semibold text-gray-800 mb-2"><Music size={16} className="text-primary" /><span>{t.experience.tryDemos.refTitle}</span></div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {AC_TRY_DEMOS.map(demo => (
                          <div key={demo.id} className="p-2 bg-white rounded-lg border border-gray-200 flex flex-col">
                            <LazyVideo src={demo.videoUrl} className="w-full aspect-video object-contain bg-black" controls playsInline crossOrigin="anonymous" />
                            <div className="mt-2 p-2 rounded-md border border-gray-200 bg-white w-full">
                              <div className="text-sm sm:text-base text-gray-800 font-medium truncate" title={demo.title}><span className="mr-1">Reference audio:</span><strong>{demo.title.replace(' (input for reference)','')}</strong></div>
                              <audio src={demo.audioUrl} controls className="w-full mt-2" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Examples Section - Different content for each tab */}
                  {false && (
                <>
                  {/* 音效生成 Tab Examples - 根据 genMode 只展示一个模块 */}
                  {activeTab === 'gen' && (
                    <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    
                    {/* Module 1: Text to Audio - 高度与右侧 OUTPUT 看板一致 */}
                    {genMode === 'text' && (
                    <div className="w-full md:col-span-3 flex flex-col min-h-[360px] p-0 bg-transparent border-none shadow-none">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 overflow-y-auto pr-1">
                            {TEXT_EXAMPLES.map((item) => {
                                const isPlaying = playingId === item.id;
                                const duration = audioDurations[item.id] || 0;
                                const currentTime = audioCurrentTime[item.id] || 0;
                                const progress = audioProgress[item.id] || 0;
                                
                                return (
                                    <div
                                      key={item.id}
                                      onClick={() => {
                                        // 点击卡片：填入提示与配置；播放仅在用户点击时触发（符合浏览器自动播放策略）
                                        setTextModePrompt(lang === 'en' && (item as any).promptEn ? (item as any).promptEn : ((item as any).prompt || item.text));
                                        setConfig(prev => ({
                                          ...prev,
                                          model: (item as any).model || PRIMARY_MODEL,
                                          durationMode: 'seconds',
                                          duration: (item as any).duration || 5,
                                          count: (item as any).count || 1,
                                        }));
                                        togglePlay(item.id, item.url);
                                      }}
                                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-primary hover:shadow-sm transition-all cursor-pointer group"
                                    >
                                      {/* 左侧图标 */}
                                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-lg flex-shrink-0">
                                        {'icon' in item && item.icon}
                                      </div>

                                      {/* 中间文案与进度 */}
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-gray-800 mb-1 truncate">
                                          {lang === 'en' && item.textEn ? item.textEn : item.text}
                                        </div>
                                        <div className="relative h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                          <div
                                            className="absolute left-0 top-0 h-full bg-primary transition-all duration-100"
                                            style={{ width: `${progress}%` }}
                                          ></div>
                                        </div>
                                      </div>

                                      {/* 右侧播放 / 时间 */}
                                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            togglePlay(item.id, item.url);
                                          }}
                                          className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 transition-colors"
                                        >
                                          {isPlaying ? (
                                            <Pause size={12} fill="currentColor" />
                                          ) : (
                                            <Play size={12} fill="currentColor" />
                                          )}
                                        </button>
                                        <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                          {formatTime(currentTime)} / {formatTime(duration)}
                                        </span>
                                      </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    )}

                    {/* Module 2: Video to Audio - 网格布局展示所有视频示例 */}
                    {genMode === 'video' && videoTab === 'plain' && (
                    <div className="w-full md:col-span-3 flex flex-col min-h-[260px] p-0 bg-transparent border-none shadow-none">
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-y-auto pr-1">
                            {VIDEO_EXAMPLES.map((video) => {
                                const isPlaying = videoAudioPlayingStates[video.id] || false;
                                
                                return (
                                    <div
                                        key={video.id}
                                        className="relative rounded-lg overflow-hidden bg-black group aspect-video"
                                    >
                                        <LazyVideo
                                            ref={(el) => {
                                                if (el) {
                                                    videoPlayerRefs.current[video.id] = el;
                                                }
                                            }}
                                            src={video.url}
                                            className="object-contain opacity-90"
                                            loop
                                            muted={!isPlaying}
                                            autoPlay
                                            playsInline
                                            crossOrigin="anonymous"
                                        />
                                        {/* Caption */}
                                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                                            <p className="text-white text-sm font-medium">{video.title}</p>
                                            {video.desc && (
                                                <p className="text-white/70 text-[10px]">{video.desc}</p>
                                            )}
                                        </div>
                                        {/* Bottom Right Buttons Container */}
                                        <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
                                            {/* Audio Play Button */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleVideoAudio(video.id);
                                                }}
                                                className="p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-all"
                                                title={isPlaying ? "静音" : "播放音频"}
                                            >
                                                {isPlaying ? (
                                                    <Volume2 size={16} />
                                                ) : (
                                                    <VolumeX size={16} />
                                                )}
                                            </button>
                                            {/* 体验同款按钮 - 视频生音效：清空所有内容后上传视频 */}
                                            <button
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    // 先清空所有内容（视频、音频、文本）
                                                    clearAllInputs();
                                                    
                                                    try {
                                                        const response = await fetch(video.url);
                                                        const blob = await response.blob();
                                                        const fileName = video.url.split('/').pop() || 'video.mp4';
                                                        const file = new File([blob], decodeURIComponent(fileName), { type: blob.type });
                                                        if (videoModeVideoPreviewUrl) URL.revokeObjectURL(videoModeVideoPreviewUrl);
                                                        setVideoModeVideoFile(file);
                                                        setVideoModeVideoPreviewUrl(URL.createObjectURL(blob));
                                                        setVideoModeVideoError(false);
                                                    } catch (error) {
                                                        console.error('Failed to load video:', error);
                                                    }
                                                }}
                                                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs font-medium transition-colors backdrop-blur-sm"
                                                title="体验同款"
                                            >
                                                体验同款
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    )}

                    {/* Module 3: Reference Audio to Audio */}
                    {genMode === 'ref' && (
                    <div className="bg-white rounded-xl p-3 flex flex-col h-64">
                        <div className="flex-1 relative rounded-lg overflow-hidden bg-black group">
                           <LazyVideo
                                ref={refVideoPlayerRef}
                                key={REF_EXAMPLES[refIndex].id}
                                src={REF_EXAMPLES[refIndex].url}
                                className="object-cover opacity-90"
                                loop
                                muted={!isRefVideoAudioPlaying}
                                autoPlay
                                playsInline
                                crossOrigin="anonymous"
                            />
                            {/* Overlay Controls */}
                            <div className="absolute inset-0 flex items-center justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={prevRef} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm">
                                    <ChevronLeft size={16} />
                                </button>
                                <button onClick={nextRef} className="p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 backdrop-blur-sm">
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                             {/* Caption & Ref Icon */}
                             <div className="absolute top-2 right-2 bg-orange-500/90 text-white text-[10px] px-2 py-0.5 rounded flex items-center gap-1 backdrop-blur-sm shadow-sm z-10">
                                 <Volume2 size={8} /> 参考音频输入
                             </div>
                            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                                <p className="text-white text-sm font-medium">{REF_EXAMPLES[refIndex].title}</p>
                                <p className="text-white/70 text-[10px]">{REF_EXAMPLES[refIndex].desc}</p>
                          </div>
                          {/* Bottom Right Buttons Container */}
                          <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
                            {/* Audio Play Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (refVideoPlayerRef.current) {
                                  if (isRefVideoAudioPlaying) {
                                    refVideoPlayerRef.current.muted = true;
                                    setIsRefVideoAudioPlaying(false);
                                  } else {
                                    refVideoPlayerRef.current.muted = false;
                                    setIsRefVideoAudioPlaying(true);
                                    refVideoPlayerRef.current.play().catch(() => {
                                      // Handle autoplay restrictions
                                      setIsRefVideoAudioPlaying(false);
                                    });
                                  }
                                }
                              }}
                              className="p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-all"
                              title={isRefVideoAudioPlaying ? "静音" : "播放音频"}
                            >
                              {isRefVideoAudioPlaying ? (
                                <Volume2 size={16} />
                              ) : (
                                <VolumeX size={16} />
                              )}
                            </button>
                            {/* 体验同款按钮 - 参考音频生音效：清空所有内容后上传视频和音频 */}
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                // 先清空所有内容（视频、音频、文本）
                                clearAllInputs();
                                
                                const currentRef = REF_EXAMPLES[refIndex];
                                try {
                                  const videoResponse = await fetch(currentRef.url);
                                  const videoBlob = await videoResponse.blob();
                                  const videoFileName = currentRef.url.split('/').pop() || 'video.mp4';
                                  const refVideoFile = new File([videoBlob], videoFileName, { type: videoBlob.type });
                                  setRefModeVideoFile(refVideoFile);
                                  setRefModeVideoPreviewUrl(URL.createObjectURL(videoBlob));
                                  setRefModeVideoError(false);

                                  const audioUrl = currentRef.audioUrl || demofile("mp3/cat.wav");
                                  const audioResponse = await fetch(audioUrl);
                                  const audioBlob = await audioResponse.blob();
                                  const audioFileName = audioUrl.split('/').pop() || "cat.wav";
                                  const refAudioFile = new File([audioBlob], decodeURIComponent(audioFileName), { type: audioBlob.type });
                                  setRefModeAudioFile(refAudioFile);
                                  setRefModeAudioPreviewUrl(URL.createObjectURL(audioBlob));
                                } catch (error) {
                                  console.error('Failed to load files:', error);
                                }
                              }}
                              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs font-medium transition-colors backdrop-blur-sm"
                              title="体验同款"
                            >
                              体验同款
                            </button>
                          </div>
                       </div>
                    </div>
                    )}

                </div>
              )}

                  {/* 音频编辑 Tab Examples */}
                  {activeTab === 'edit' && (
                    <div className="w-full mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                      {/* 编辑示例模块1 */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-col h-64">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                            <Video size={12} />
                          </div>
                          <h3 className="font-bold text-sm text-gray-900">音频提取</h3>
                        </div>
                        <div className="flex-1 relative rounded-lg overflow-hidden bg-black group">
                          <LazyVideo
                            src={demofile(`mp4/${encodeURIComponent("音频提取.mp4")}`)}
                            className="object-cover opacity-90"
                            loop
                            muted
                            controls
                            playsInline
                            onPlay={(e) => { e.currentTarget.muted = false; }}
                          />
                        </div>
                      </div>

                      {/* 编辑示例模块2 */}
                      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-col h-64">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                            <Video size={12} />
                          </div>
                          <h3 className="font-bold text-sm text-gray-900">音频删除</h3>
                        </div>
                        <div className="flex-1 relative rounded-lg overflow-hidden bg-black group">
                          <LazyVideo
                            src={demofile(`mp4/${encodeURIComponent("音频删除.mp4")}`)}
                            className="object-cover opacity-90"
                            loop
                            muted
                            controls
                            playsInline
                            onPlay={(e) => { e.currentTarget.muted = false; }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                </>
                  )}
                </>
              </div>
          </div>

          {/* Prompt guide modal */}
          {showPromptGuide &&
            createPortal(
              <div
                className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                role="dialog"
                aria-modal="true"
                aria-labelledby="prompt-guide-title"
                onClick={() => setShowPromptGuide(false)}
              >
                <div
                  className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-gray-100 p-6 text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setShowPromptGuide(false)}
                    className="absolute top-4 right-4 p-1.5 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                    aria-label={lang === 'zh' ? '关闭' : 'Close'}
                  >
                    <X size={18} />
                  </button>
                  <div className="flex items-center gap-2 mb-4 pr-10">
                    <div className="p-2 rounded-xl bg-amber-50 text-amber-600">
                      <Lightbulb size={22} />
                    </div>
                    <h2 id="prompt-guide-title" className="text-lg font-bold text-gray-900">
                      {lang === 'zh' ? '音效生成 · 提示词指南' : 'Audio Generation · Prompt Guide'}
                    </h2>
                  </div>
                  {lang === 'zh' ? (
                    <>
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        以下为各模式下如何撰写「提示词」与「负向提示」的实用说明。
                      </p>
                      <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">文生音效</h3>
                          <ul className="text-gray-600">
                            写清<strong>场景</strong>（室内/户外、时间、天气）、<strong>发声主体</strong>与<strong>动作/材质</strong>（如Birds singing in a forest，Motorcycle idle and accelerate）。
                          </ul>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">视频生音效</h3>
                          <p className="text-gray-600">
                            上传视频后，模型会结合画面生成音效。在「提示词」中可<strong>补充创意</strong>（如强调环境氛围、材质、动作细节）；留空则更依赖画面本身。
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">文本控制视频生音效</h3>
                          <p className="text-gray-600">
                            需上传<strong>视频</strong>并填写<strong>提示词</strong>。提示词中可加入明确的目标或风格（例如：chainsaw idling → 生成“电锯怠速”声），用于在视频基础上更精确地控制生成结果。
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">参考音频生音效</h3>
                          <p className="text-gray-600">
                            需同时上传<strong>视频</strong>与<strong>参考音频</strong>。（例如：上传一段战鼓声音作为参考音频，然后为视频生成相似的音色。）。
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">负向提示</h3>
                          <p className="text-gray-600">
                            写明<strong>不希望出现</strong>的内容，例如：人声、背景音乐、某种噪声（电流声、风声）、与场景不符的音效等，有助于减少跑偏。
                          </p>
                        </section>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        Practical tips for writing prompts and negative prompts in each mode.
                      </p>
                      <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">Text-to-Audio</h3>
                          <ul className="text-gray-600">
                              Describe the <strong>scene</strong> (indoor/outdoor, time, weather), the <strong>sound source</strong>, and <strong>action/material</strong> (e.g. birds singing in a forest, Motorcycle idle and accelerate).
                          </ul>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">Video-to-Audio</h3>
                          <p className="text-gray-600">
                            After uploading a <strong> video </strong>, the model uses visuals. Optionally add a hint in &quot;Prompt&quot; to help the model generate more accurate audio; leave it empty to rely on the video alone.
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">Text-Controlled Video-to-Audio</h3>
                          <p className="text-gray-600">
                            Upload both a <strong>video</strong> and <strong>prompt</strong>.  In the prompt, add a creative hint in &quot;Prompt&quot; (e.g. generate a chainsaw idling sound for the video using the prompt "chainsaw idling").
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">Audio-Controlled Video-to-Audio</h3>
                          <p className="text-gray-600">
                            Upload both a <strong>video</strong> and <strong>reference audio</strong>. (e.g. upload a war drum sound as the reference audio, then generate a similar timbre for the video).
                          </p>
                        </section>
                        <section>
                          <h3 className="font-semibold text-gray-900 mb-1.5">Negative prompt</h3>
                          <p className="text-gray-600">
                            List what you <strong>do not want</strong>: speech, music, certain noise (hum, wind), or mismatched effects.
                          </p>
                        </section>
                      </div>
                    </>
                  )}
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPromptGuide(false)}
                      className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-95 transition-opacity"
                    >
                      {lang === 'zh' ? '我知道了' : 'Got it'}
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

          {/* Toast：固定视口顶端，不随页面滚动 */}
          {toastShow && (toastForMode === null || toastForMode === genMode) && createPortal(
            <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 px-5 py-3 text-white rounded-full shadow-lg ${toastForMode === null ? 'bg-amber-600' : 'bg-gray-800'}`}>
              {toastForMode === null ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 flex-shrink-0">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              ) : (
                <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{toastMessage}</span>
            </div>,
            document.body
          )}

      </div>
    </div>
  );
}
